import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { themeVar as colors, type as typeTokens } from "../design-tokens";
import { NEVER_DISPLAYED_KEYS, SUBJECT_FIELD_SCHEMA } from "./fieldSchema";
import { CONDITIONS, CONDITION_LABELS, SALE_TYPES, SALE_TYPE_LABELS, type Condition,
         concessionStatusAfterHumanEdit, normalizeCondition } from "./constants";
import { StreetViewCell } from "./StreetViewCell";
import { CompNumInput } from "./CompNumInput";
import { CompCards } from "./CompCards";
import { cellProvenance, isProviderRow } from "./cascade";
import { compMissingFields, COMP_REQUIRED_FIELDS } from "./ReadinessGate";
import { availablePositions, clearLadder, displayOrder, ladderIndices, LEAD_SHORT, setLeadFromRanking, stepLead, moveLead,
         leadOf, setLead, WEIGHT_NOTE, type LeadPosition } from "./compLadder";
import { acresConflictMessage } from "./subjectGrid";
import { LotConflictPicker } from "./LotConflictPicker";
import { LOT_UNRESOLVED_WORDS } from "./lotConflict";
import { currentDaysOnMarket, cumulativeDaysOnMarketLabel, daysOnMarketLabel } from "./daysOnMarket";
import type { CandidateComp, SubjectFieldDetail, SubjectGrid } from "./types";
import { composeHoa, HoaControl, InfoButton } from "./SubjectControls";
import { measure } from "../ui/measures";
import { blockStyle } from "../ui/Block";
import { FieldHistoryDisclosure } from "./FieldHistoryDisclosure";
import type { CadFieldState, CadPick } from "./cadPick";
import { awaitingReview } from "./cadPick";

/**
 * The confirm-everything subject grid (VI-3 build detail — locked spec,
 * `valubrief-ui-remediation-audit-v0.61.09.md` Part VI). A PURE controlled component: all state
 * lives in the parent (`CmaAmbFlow`); this file only renders `grid` and reports edits upward.
 *
 * Rules enforced here, straight from the spec:
 *  - Found fields are pre-checked + filled, each with an honest source tag (never "County record"
 *    standing in for a Cotality figure — the H1c fix this project's docs call out by name).
 *  - Not-found fields are unchecked + blank, rendered as a dash, never a zero.
 *  - Any field can be checked/unchecked/edited by the human at any time.
 *  - "Save builds the subject only from CHECKED, source-tagged fields" — enforced by the PARENT
 *    (CmaAmbFlow only sends `grid[key]` into the submit payload when `.checked` is true); this
 *    component's job is just to make that state legible and editable.
 *  - Condition is a SEPARATE control, defaulted "average," never derived from data.
 *
 * Session 8 additions (board doc `06_OWNER_DIRECTION_editable-personas-scope.md` §2 "Item A" — "the
 * agent must be able to correct any number... Cotality is close but not accurate"):
 *  - Editing a field that already carries a real source (not "You entered") now preserves the
 *    as-pulled value in `originalValue` and flags `edited: true` on first divergence, instead of
 *    silently letting the source tag keep claiming a number the source never said. See
 *    `types.ts`'s `SubjectFieldState` docstring for the full rationale.
 *  - The comp table's sold price / GLA are now editable (were display-only), plus new editable
 *    year / beds+baths / lot columns — the full override set the board doc calls out, same
 *    provenance discipline (`_raw` original preserved on first edit).
 *  - A Street View reference-photo cell renders per comp row (labeled, display-only, never claimed
 *    as part of the deliverable) — see `StreetViewCell.tsx`.
 */
/** What one step of the condition ladder is worth on THIS set of sales, in dollars.
 *
 * Derived from the comps on screen, never hardcoded: `engine.amb` applies
 * PARAMS["condition_step"] (5%) of each comp's OWN sale price per step, so the figure is a
 * property of the price band being worked in and a constant here would be wrong the first time
 * someone valued a $900,000 house. Returns "" when there is nothing to average — an empty grid
 * gets the rule without a fabricated number, which is the same bar every other figure in this
 * file is held to. Rounded to the nearest $1,000 and phrased as "about", because it is an
 * indication of scale and presenting it to the dollar would imply a precision it does not have. */
function conditionStepHint(comps: CandidateComp[]): string {
  const prices = comps
    .filter((c) => c._selected !== false)
    .map((c) => c.price)
    .filter((p): p is number => typeof p === "number" && Number.isFinite(p) && p > 0);
  if (!prices.length) return "";
  const mean = prices.reduce((a, b) => a + b, 0) / prices.length;
  const step = Math.round((mean * 0.05) / 1000) * 1000;
  if (!step) return "";
  return ` — on these sales, roughly $${step.toLocaleString()} per step`;
}

/** A field the human has actually confirmed: ticked AND carrying a value. The same test
 * `ReadinessGate.missingRequiredFields` applies — written here rather than imported to avoid a
 * cycle (ReadinessGate already imports from this file). */
function isConfirmed(grid: SubjectGrid, key: string): boolean {
  const f = grid[key];
  return !!f?.checked && f.value != null && f.value !== "";
}

/** One confirmed field, as a reader sees it in the quiet summary. A blank is a dash, never a zero
 * and never a blank cell that could be mistaken for a value of nothing. */
function displayValue(state: SubjectGrid[string] | undefined, digits?: number): string {
  if (!state || state.value == null || state.value === "") return "—";
  if (typeof state.value === "boolean") return state.value ? "Yes" : "No";
  if (digits != null && typeof state.value === "number") return state.value.toFixed(digits);
  return String(state.value);
}

/**
 * A FILLED ROW THAT IS NOT A PLAIN READING OF THE RECORD SAYS SO (2026-10-19, owner). A Dallas lot
 * whose unit DCAD records as UNASSIGNED is served in square feet only where frontage × depth
 * confirms it; that row carries the state "inferred unit" and the note naming the check, and the
 * acreage computed from it carries "computed". The state comes from the picked record's own
 * `field_states`, and only while the row still carries that record's tag: once the agent edits it,
 * the value is theirs ("You entered") and the state no longer describes it.
 */
function pickState(cadPick: CadPick | null | undefined, key: string,
                   state: SubjectGrid[string] | undefined): CadFieldState | null {
  if (!cadPick || !state || state.edited || !state.sourceTag?.startsWith(cadPick.cadLabel)) return null;
  return cadPick.fieldStates?.[key] ?? null;
}

/** A computed acreage is shown to three decimals (display only; the stored value is untouched). */
const COMPUTED_DIGITS = 3;

/** The widest a subject row's source column may grow before its text wraps (px). */
const SOURCE_MAX_W = 220;

/* ------------------------------------------------------------------------------------------- *
 * THE COMPARABLE TABLE'S COLUMNS — one list, in render order (2026-08-10, owner: "we also need to
 * be able to click on the comp columns and sort if we want to", "column widths need to be
 * adjustable", "sales date move to the top of the priority", "the comps need to show more
 * details during the review").
 *
 * The header cells, the `<colgroup>` the resize handles drive, and the sort vocabulary all read
 * from this one table, so a column cannot end up sortable-but-unlabelled or resizable-but-absent.
 * The BODY cells are still written out by hand below, because each one is a different control
 * (an input, a select, a read-only fact with its own provenance line) and generating them from a
 * table would cost more than it saved — the order of the two lists has to be kept in step by
 * reading them side by side, which is why they sit in the same file.
 * ------------------------------------------------------------------------------------------- */

/** Every attribute this grid can be ordered by. `days_on_market` and `baths` are derived rather
 * than raw fields, which is exactly why sorting reads through this vocabulary instead of indexing
 * a comp by column key. */
type CompSortKey =
  | "sale_date" | "name" | "subdivision" | "distance" | "days_on_market" | "price"
  | "seller_concession" | "gla" | "beds" | "year" | "baths" | "lot_sf" | "garage" | "pool"
  | "condition" | "sale_type" | "source";

interface CompColumn {
  key: string;
  label: string;
  /** Omitted for a column there is no honest order for (the checkbox). */
  sort?: CompSortKey;
  /** The width this column starts at, in px — also the base a keyboard resize counts from before
   * the browser has laid the table out (jsdom measures every element as zero, so a test that
   * resizes a column has to have something real to count from). */
  width: number;
  /** The checkbox column is not resizable: it holds one fixed-size control and dragging it would
   * only ever make the row harder to hit. */
  fixed?: boolean;
}

const COMP_VIEW_KEY = "vb:cma:comp-view";

/** The order the rows ARRIVE in. On the cascade path that is the search's merge order, not
 * `rank_comps` (never called there): MLS sales nearest first (`ntreis_search`), then the
 * public-record and county rows, held rows last. The label and hint say exactly that.
 * NEEDS OWNER: sort label wording (comps-ux fix round, 2026-10-25). */
export const SEARCH_ORDER_LABEL = "Nearest first, then county records";
export const BEST_MATCH_LABEL = "Best match";
/** "Why this order", in plain words. Read off `engine.amb.rank_comps` and `_review_score`, not
 * remembered: arm's-length sales first, then distance, sale recency, same subdivision, living area
 * (the heaviest physical term), lot size, age and assessor condition. It states no weight, because
 * a weight on screen would be a number with no source a person can check. */
export const BEST_MATCH_WHY =
  "Best match puts first the sales most like this home. Completed, arm's-length sales come before the rest. " +
  "Among those, a sale ranks higher the closer it is, the more recently it sold, if it is in the same subdivision, " +
  "and the nearer it is in living area, lot size, age and condition. It is the same ranking ValuBrief uses to " +
  "suggest comparables on an uploaded report. On a new set of sales the top three that can price a report are " +
  "ticked to start; change any of it. The order never changes a price: only the sales you tick do.";

/** True when `order` is every index 0..n-1 exactly once. */
function isPermutation(order: number[] | null | undefined, n: number): order is number[] {
  if (!Array.isArray(order) || order.length !== n) return false;
  const seen = new Set<number>();
  for (const i of order) {
    if (!Number.isInteger(i) || i < 0 || i >= n || seen.has(i)) return false;
    seen.add(i);
  }
  return true;
}
export const SEARCH_ORDER_HINT =
  "The order the search found them: MLS sales nearest first, then county and public-record sales. It is not a ranking by similarity.";

/** The orders worth offering on the cards. Every one is a column the table can sort by; the ones
 *  left out (source, sale type, pool) are not what anybody reorders a comparable set by. */
const CARD_SORTS: CompSortKey[] = [
  "sale_date", "price", "gla", "distance", "year", "lot_sf", "days_on_market", "name",
];

const COMP_COLUMNS: CompColumn[] = [
  { key: "select", label: "", width: 34, fixed: true },
  /* WHICH SALE LEADS THE BRIEF (2026-09-08, owner: "allow the agent to select the comp that is the
     primary secondary and 3rd closest comp. we auto rank but the agent can override").

     SECOND, BESIDE THE TICK, because it is the other thing the agent DECIDES about a row — the
     tick says whether this sale is in, this says where it stands — and both belong together and
     ahead of the facts they are decided from. It carries no sort: "order by the order" is not a
     question, and the ladder already sits at the top of every ordering (`displayOrder`). */
  { key: "lead", label: "Order", width: 96 },
  /* SALE DATE LEADS THE ROW (2026-08-10, owner: "sales date move to the top of the priority").
     It used to be nowhere on this screen at all — the extractor has asked for it since 2026-08-05
     and `engine.amb._sale_ordinal` reads it for recency, but the review grid dropped it, so the
     agent chose which sales to price from without being shown WHEN any of them sold. It is the
     first attribute on the row now, ahead of the neighbourhood that used to lead; the address
     stays in the next column because it is the row's identity rather than one of its facts. */
  { key: "sale_date", label: "Sale date", sort: "sale_date", width: 118 },
  /* "Address (A-Z)", NOT "Comp" (2026-10-25, owner): this column sorts by the `name` key, which is the
     street address, alphabetically. The old heading "Comp" read as our comparable ranking, and it
     is not. The arrival order is "Nearest first, then county records" in the Order by list. */
  { key: "name", label: "Address (A-Z)", sort: "name", width: 256 },
  /* NEIGHBOURHOOD AND DISTANCE, both read-only (2026-08-04, owner).
     Neighbourhood is what tells an agent whether a sale is even in the same pocket of the market;
     distance is "listed, but not adjustable" — a typed distance would be a claim about geography,
     and geography is not an opinion. Distance is only present on a provider-sourced candidate; an
     uploaded MLS row has no coordinates in this pipeline and honestly shows a dash. */
  { key: "subdivision", label: "Neighborhood", sort: "subdivision", width: 140 },
  { key: "distance", label: "Distance", sort: "distance", width: 86 },
  /* SPELLED OUT, NEVER "DOM" (2026-08-04, owner): "never say DOM on the worksheet". The figure was
     in the report and nowhere on the screen where the agent actually chooses which sales to price
     from. */
  { key: "days_on_market", label: "Days on market", sort: "days_on_market", width: 118 },
  { key: "price", label: "Sold price", sort: "price", width: 132 },
  { key: "seller_concession", label: "Seller concession", sort: "seller_concession", width: 132 },
  { key: "gla", label: "GLA", sort: "gla", width: 110 },
  /* BEDS (2026-08-10). Read by the extractor on every row since the CDOM work and rendered only in
     the competing-listings table — so the agent got a bedroom count for the homes they are up
     against and none for the sales the value is built from. Display only, and it must stay that
     way: `engine.amb._lines` has no bedroom term, because bedroom count is already inside living
     area and pricing it twice would double-count it. */
  { key: "beds", label: "Beds", sort: "beds", width: 62 },
  { key: "year", label: "Year", sort: "year", width: 74 },
  { key: "baths", label: "Baths (F/H)", sort: "baths", width: 108 },
  { key: "lot_sf", label: "Lot (SF)", sort: "lot_sf", width: 100 },
  /* GARAGE (2026-08-04). `engine.amb.validate_comp` has always required this field and it has
     never had a column, so a comp missing it was silently set aside at build time and the report
     priced off fewer sales than were ticked. */
  { key: "garage", label: "Garage", sort: "garage", width: 78 },
  /* POOL (2026-08-10) — READ-ONLY, AND THAT IS A GAP WORTH NAMING.
     `engine.amb` prices a pool difference at max($25,000, 5% of that comp's sale price), reading
     the comp's own `pool` flag, and this grid showed the flag nowhere: the single largest
     feature adjustment in the engine was invisible on the screen that exists to check the
     numbers. Showing it is strictly better than not. Correcting it is not possible here yet —
     `pool` is a non-nullable boolean on the wire while the extractor can genuinely return "not
     stated", and a checkbox that renders unknown as unticked would assert "no pool" on a file
     that never said. Three honest states are rendered; the editable control needs the nullable
     seam first. */
  { key: "pool", label: "Pool", sort: "pool", width: 74 },
  { key: "condition", label: "Condition", sort: "condition", width: 150 },
  { key: "sale_type", label: "Sale type", sort: "sale_type", width: 170 },
  { key: "source", label: "Source", sort: "source", width: 120 },
];

/**
 * The sum of every declared column width — the table's true intrinsic width, and therefore its
 * `min-width`.
 *
 * DERIVED, NEVER TYPED. It used to be a hand-written `minWidth: 1180`, which was 882px short of
 * what the columns actually declare, and that gap was the whole of the 2026-08-29 layout collapse:
 * a table told to fit in less room than its own columns need will take the difference out of the
 * text columns. Computing it here means adding a nineteenth column cannot re-open that defect.
 */
const COMP_COLUMNS_TOTAL_WIDTH = COMP_COLUMNS.reduce((sum, c) => sum + c.width, 0);

/**
 * WHICH BAND EACH COLUMN SITS UNDER. The four ideas the prototype groups this table into.
 * A key absent from here is not an error — it lands in the trailing unnamed band, which is the
 * safe direction: a new column appears with no band rather than silently under the wrong one.
 */
const COMP_COLUMN_GROUPS: Record<string, string> = {
  select: "Which sale",
  lead: "Which sale",
  sale_date: "Which sale",
  name: "Which sale",
  subdivision: "Which sale",
  distance: "Which sale",
  days_on_market: "As it sold",
  price: "As it sold",
  seller_concession: "As it sold",
  gla: "What the record says",
  beds: "What the record says",
  year: "What the record says",
  baths: "What the record says",
  lot_sf: "What the record says",
  garage: "What the record says",
  pool: "What the record says",
  condition: "What the record says",
  sale_type: "Where it came from",
  source: "Where it came from",
};

/** Consecutive runs of one group, counted off the LIVE column list so the two header rows can
 *  never disagree about how many cells there are. */
const compGroupBands: { label: string; span: number }[] = COMP_COLUMNS.reduce(
  (bands: { label: string; span: number }[], col) => {
    const label = COMP_COLUMN_GROUPS[col.key] ?? "";
    const last = bands[bands.length - 1];
    if (last && last.label === label) last.span += 1;
    else bands.push({ label, span: 1 });
    return bands;
  },
  [],
);


/** The narrowest a column may be dragged. Below this a header label is unreadable and the cell's
 * own control is clipped, which is a worse table than the one the agent started with. */
const MIN_COL_WIDTH = 48;

/** Keys that say where a figure CAME FROM rather than what it is — excluded from the `_raw`
 * override snapshot. See `editComp`. */
const COMP_PROVENANCE_KEYS = new Set<string>(["seller_concession_source", "concession_status"]);

/** One comp's value for one sortable column, in a form two rows can be compared by. `null` means
 * "this row has nothing to say here" and is sorted to the BOTTOM in both directions — a blank is
 * not a small number, and burying the rows that still need attention under a descending sort would
 * be the opposite of useful. */
function compSortValue(c: CandidateComp, key: CompSortKey): string | number | null {
  const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
  const s = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim().toLowerCase() : null);
  switch (key) {
    // ISO dates sort correctly as text, including the `YYYY-MM` form the extractor returns when
    // the record printed only a month — so no Date object is constructed and no timezone can
    // shift a sale into the wrong month.
    case "sale_date": return s(c.sale_date);
    case "name": return s(c.name);
    case "subdivision": return s(c.subdivision);
    case "distance": return n(c.distance);
    // The figure the CELL shows, not the raw field: a competing listing's exposure is brought
    // forward to today, and an order that disagreed with the printed column would read as a bug.
    case "days_on_market": return currentDaysOnMarket(c, c.dom_as_of);
    case "price": return n(c.price);
    case "seller_concession": return n(c.seller_concession);
    case "gla": return n(c.gla);
    case "beds": return n(c.beds);
    case "year": return n(c.year);
    case "baths": {
      const f = n(c.full_baths);
      const h = n(c.half_baths);
      if (f === null && h === null) return null;
      return (f ?? 0) + (h ?? 0) * 0.5;
    }
    case "lot_sf": return n(c.lot_sf);
    case "garage": return n(c.garage);
    case "pool": return typeof c.pool === "boolean" ? (c.pool ? 1 : 0) : null;
    // BY THE LADDER, NOT THE ALPHABET. `CONDITIONS` is ordered best-to-worst and that order is what
    // the adjustment actually moves on; sorting "as_is" above "renovated" because A precedes R
    // would be a sort that means nothing.
    case "condition": {
      // SORT ON WHAT THE CELL SHOWS (2026-08-13). This read the raw field, so a comp with no
      // condition returned null and sorted into the "unknown" bucket — while its cell displayed a
      // rung and the engine priced it as "average". Three different answers for one comp. The cell,
      // the sort and the arithmetic now all read the same normalized value.
      return (CONDITIONS as readonly string[]).indexOf(normalizeCondition(c.condition));
    }
    // Same reasoning: `SALE_TYPES` runs arm's-length types first, then the excluded ones, so
    // sorting by it groups the sales that cannot price a report together at one end.
    case "sale_type": {
      const i = (SALE_TYPES as readonly string[]).indexOf(c.sale_type);
      return i === -1 ? null : i;
    }
    /* BY THE SOURCE'S OWN NAME (fixed 2026-08-11 — see `isProviderRow` in `cascade.ts`).
       This read `c._source === "cotality" ? 1 : 0`, and the server has never written that value:
       `cotality.SOURCE_TAG` is "Cotality (public-record aggregate)". So every row scored 0 and
       "sort by source" was a no-op that looked like a working control. `_source` IS the printed
       label, so ordering by it groups the rows exactly as the column reads — and hardcodes no
       provider string, which is what let the old comparison rot unnoticed. */
    case "source": return s(c._source);
  }
}

/** Which way the FIRST click on a column sorts. Ascending everywhere except the sale date, which
 * leads most-recent-first: that is the priority the owner asked for, and an agent who clicks
 * "Sale date" wants the freshest sales at the top, not the oldest. */
function defaultSortDir(key: CompSortKey): "asc" | "desc" {
  return key === "sale_date" ? "desc" : "asc";
}

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
                     "July", "August", "September", "October", "November", "December"];

/** The sale date as a reader sees it. `YYYY-MM-DD` becomes "May 5, 2026"; the `YYYY-MM` form the
 * extractor returns when the record printed only a month becomes "May 2026" — never a day we were
 * not given. Anything that does not match either shape is printed VERBATIM rather than reshaped
 * into a date we cannot vouch for, and a missing date is a dash, never today's. */
export function saleDateLabel(value?: string | null): string {
  const raw = value == null ? "" : String(value).trim();
  if (!raw) return "—";
  /* THE COMPACT FORM IS A REAL PROVIDER FORMAT, NOT A MALFORMED ONE (2026-08-29, owner, from a
     live Denver run where the Sale date column read `20251201` and `20260731`).

     County recorder extracts and several aggregator feeds emit `YYYYMMDD` with no separators. This
     only matched the hyphenated spelling, so the compact one fell through to `return raw` and the
     grid printed an eight-digit integer in the column the owner had specifically asked to lead the
     row — on the screen where an agent judges how recent a sale is. It reads as a parcel number,
     not a date, and "December 2025" versus "20251201" is the difference between seeing recency and
     decoding it.

     Widened rather than replaced: the hyphenated form still parses exactly as before, and anything
     this still cannot read is returned untouched rather than guessed at. */
  const m = /^(\d{4})-?(\d{2})(?:-?(\d{2}))?$/.exec(raw);
  if (!m) return raw;
  const month = MONTH_NAMES[Number(m[2]) - 1];
  if (!month) return raw;
  return m[3] ? `${month} ${Number(m[3])}, ${m[1]}` : `${month} ${m[1]}`;
}

export function ConfirmGrid({
  grid,
  onChange,
  condition,
  onConditionChange,
  comps,
  onCompsChange,
  addressLine,
  sections = "all",
  minComps,
  hideKeys,
  quietKeys,
  quietTitle,
  quietBlurb,
  optionalKeys,
  showBathNote = true,
  conditionMode = "ask",
  hideReferencePhoto = false,
  rungLabels,
  defaultCompView = "table",
  blankNote,
  cadPick,
  compFocus,
  briefKey,
  bestOrder,
  bestOrderNote,
}: {
  /** C1 "BEST MATCH" (2026-11-02): the engine's own ranking of `comps` (`rank_comps`, from
   * `POST /api/next/comp-search/rank`), as indices into `comps`. `undefined` = this screen does not
   * offer Best match (the option is not drawn). `null` = offered but not available right now; then
   * `bestOrderNote` says why, beside the control, and the rows stay in the order they arrived. */
  bestOrder?: number[] | null;
  bestOrderNote?: string | null;
  /** The brief (job) id, used only to remember whether "More details" is open for this brief in
   * this browser session. Absent before a job exists: then it is simply closed on load. */
  briefKey?: string;
  /** 1G — the map's link to the comparable rows. `onOrder` reports the display order (indices into
   * `comps`, the order the cards are drawn in) whenever it changes; that order IS the pin numbering.
   * `activeIndex` / `onActiveChange` make hovering or focusing a row light its pin and the reverse.
   * Omitted everywhere but the comps step, where it changes nothing about the rows. */
  compFocus?: {
    activeIndex: number | null;
    onActiveChange: (index: number | null) => void;
    onOrder: (order: number[]) => void;
  };
  grid: SubjectGrid;
  onChange: (next: SubjectGrid) => void;
  /** WHAT A BLANK ROW SAYS, WHEN THE CALLER KNOWS BETTER THAN "Nothing on record" (2026-09-25). On a
   * subject read from a county appraisal sheet, a blank HOA is not a gap in the world's records --
   * that sheet format never lists one. The caller says which; undefined keeps the old words. */
  blankNote?: (key: string) => string | undefined;
  condition: string;
  onConditionChange: (next: string) => void;
  comps: CandidateComp[];
  onCompsChange: (next: CandidateComp[]) => void;
  /** The subject's address line, threaded through only for the Street View reference-photo cell —
   * this component otherwise never needs the address (it works purely off `grid`/`comps`). */
  addressLine?: string;
  /** WHICH HALVES TO RENDER (2026-08-01, wizard restructure). This component has always drawn two
   * unrelated things — the subject's own facts and the comparable table — one under the other on a
   * single very long review screen. The wizard now asks for them on two different steps, so the
   * caller says which half it wants. Defaults to "all", so every existing caller and every existing
   * test keeps the exact behaviour it had; nothing about the rendering of either half changed. */
  sections?: "all" | "subject" | "comps";
  /** The engine's closed-comp floor (`ExtractGrid.min_comps`), used ONLY by the auto-select rule
   * below to decide when to stop restoring the default selection. Falls back to the engine's
   * shipped value of 3, exactly like `ReadinessGate`. */
  minComps?: number;
  /** ASK FOR A FACT ONCE (2026-08-05, owner: "the tool makes us confirm the subject file multiple
   * times"). Subject rows whose question is owned by a DIFFERENT control on the same screen, so
   * rendering them here would be the same question twice. The only current use is `address`, which
   * step 1 asks for in its own labelled input and step 2 prints read-only in its heading.
   *
   * A hidden row is still in the grid and still counts toward every readiness gate — this hides a
   * duplicate QUESTION, never a requirement. The caller is responsible for only hiding a row whose
   * value is already confirmed; `CmaAmbFlow` and `SubjectAddressStep` both test for that before
   * passing a key in. */
  hideKeys?: string[];
  /** ROWS THE AGENT ALREADY CONFIRMED ON AN EARLIER STEP. Rendered as a quiet read-only summary
   * with one obvious control that turns them back into the full editable rows — visible, never
   * re-asked. Everything NOT in this list renders exactly as it always has, at the top, because
   * that is what is actually new or still missing.
   *
   * SAFETY: if any key in here is a REQUIRED field that is no longer confirmed (the agent unticked
   * it inside the block, say), the block force-expands and its collapse control is withdrawn. A
   * required field can never end up hidden behind a summary. */
  quietKeys?: string[];
  quietTitle?: string;
  quietBlurb?: string;
  /** OPTIONAL ROWS THE RECORD SIMPLY DID NOT CARRY (2026-08-06, owner defect).
   *
   * On the owner's live job, step 2's active question list came out as exactly two rows — "Lot size
   * (acres)" and "Half baths" — because they were the only two schema fields step 1 had not
   * confirmed. Neither is required, neither had "landed", and the record held nothing for either.
   * But they were the only rows on the screen still asking a question, so they read as the
   * outstanding work, and the agent duly filled them in: the square footage went into acres (which
   * later killed the build on a lot conflict) and the FULL bath count went into half baths (which
   * printed "3 full / 3 half" on a client's report).
   *
   * An optional field the record did not carry is NOT outstanding work. Rows named here are moved
   * out of the active list into their own collapsed block, clearly labelled as optional and as
   * something we have nothing for. They are still one click from being editable — this changes
   * where they sit and what they claim, never whether they can be answered.
   *
   * NEVER PASS A REQUIRED KEY. A genuinely unconfirmed required field must stay in the active list
   * and must keep blocking the readiness gate; `CmaAmbFlow` derives this list from
   * `SUBJECT_FIELD_SCHEMA` non-required rows only, and the filter below enforces it here too so a
   * future caller cannot bury a blocker by accident. */
  optionalKeys?: string[];
  /** "ask" renders the condition ladder as the question it is. "confirm" renders the standing
   * answer with a Change control that reveals the identical select and the identical dollar-scale
   * note — for the second screen in a row that would otherwise put the same dropdown in front of
   * an agent who already set it. */
  /** THE HALF-BATH CAUTION, SAID ONCE (owner, 2026-08-11: "it still has the half bath warnings on
   *  all the pages"). The same paragraph rendered on step 1 AND step 2 AND inside the deliverable's
   *  grid — a product arguing with itself about bathroom data in a document a client reads. The
   *  disclosure is not deleted (unknown must never read as zero, and the report now carries one
   *  sentence under the grid instead of a paragraph inside it); it is simply not repeated. Defaults
   *  true, so every existing caller and test renders exactly as before, and step 1 opts out. */
  showBathNote?: boolean;
  conditionMode?: "ask" | "confirm";
  /** The Street View reference photo is keyed on the address alone, so a second screen showing the
   * same address renders a byte-identical image. Suppressed on the second appearance. */
  hideReferencePhoto?: boolean;
  /** `rung_labels` STRAIGHT OFF THE CASCADE PAYLOAD (2026-08-11) — the rung id -> printed label
   * map, used for the ONE row-level decision that has to know whether a candidate came from the
   * provider: the server refuses to build from an unreviewed one, so this grid must never tick it
   * on the agent's behalf.
   *
   * THE PROP EXISTS BECAUSE THE HARDCODED STRING WAS WRONG. Two branches in this file compared
   * `c._source === "cotality"`; the server has never written that value (`cotality.SOURCE_TAG` is
   * "Cotality (public-record aggregate)"), so both were dead for the life of the file and neither
   * ever fired on real data. The fix is not a better literal — it is not having one. Absent (every
   * caller that has no cascade payload) the row-level question answers "I cannot tell", which
   * `isProviderRow` renders as false: the only thing it gates is an auto-selection the agent can
   * make themselves, and a wrong "yes" would suppress a control they are entitled to. */
  rungLabels?: Record<string, string>;
  /** `table` (the complete, editable surface) or `cards`. See `compView` below. */
  defaultCompView?: "table" | "cards";
  /** THE COUNTY RECORD THIS SUBJECT IS LOCKED TO, when it is (owner spec 2026-10-04 §2: "field
   * history"). Threading this through is what lets a row offer "History" instead of nothing — the
   * history route is keyed on `county` + `prop_id`, which only exists once an agent has picked a
   * CAD record, never for a Cotality or uploaded subject. Absent is the ordinary case and renders
   * no "History" control, byte-identical to this component before it existed. */
  cadPick?: CadPick | null;
}) {
  const groups = Array.from(new Set(SUBJECT_FIELD_SCHEMA.map((f) => f.group)));
  const showSubject = sections === "all" || sections === "subject";
  const showComps = sections === "all" || sections === "comps";
  const floor = minComps ?? 3;

  /**
   * WHICH SURFACE THE COMPARABLE SET IS SHOWN ON.
   *
   * Two views over one set of records, one sort and one edit path. Cards are for deciding — the
   * identity, the three figures that settle it, how it differs from the subject; the table is for
   * checking and correcting, and remains the complete surface.
   *
   * THE COMPONENT'S DEFAULT IS THE TABLE, AND THE SCREEN OPTS INTO CARDS. Not an accident: the
   * table is the view where every one of the eighteen fields is present and editable, so it is the
   * safe answer for any caller that has not thought about it. `CmaAmbFlow` passes `compView="cards"`
   * because on that screen the agent's job is to choose.
   *
   * The choice is remembered per browser, and a browser that refuses storage simply does not
   * remember it — never a blank screen because of a preference.
   */
  const [compView, setCompView] = useState<"table" | "cards">(() => {
    try {
      const saved = window.localStorage.getItem(COMP_VIEW_KEY);
      if (saved === "table" || saved === "cards") return saved;
    } catch { /* a private window, or storage blocked. Fall through to the caller's choice. */ }
    return defaultCompView;
  });

  useEffect(() => {
    try { window.localStorage.setItem(COMP_VIEW_KEY, compView); } catch { /* see above */ }
  }, [compView]);

  /* Re-read after every paint that could change either number: switching views mounts and
   * unmounts the region, dragging a divider changes `scrollWidth`, and resizing the window
   * changes `clientWidth`. `scrollWidth - clientWidth` is 0 when everything fits, which is what
   * makes the whole apparatus disappear on a wide enough monitor rather than sitting there
   * telling a reader about columns they can already see. */
  useEffect(() => {
    const read = () => {
      const el = compScrollRef.current;
      if (!el) return;
      setCompScroll({ left: el.scrollLeft, max: Math.max(0, el.scrollWidth - el.clientWidth) });
    };
    read();
    window.addEventListener("resize", read);
    return () => window.removeEventListener("resize", read);
    /* `colWidths` is deliberately NOT a dependency: it is declared below this effect and reading
       it here is a use-before-declaration. A dragged divider changes `scrollWidth`, so the numbers
       go briefly stale — the resize listener and the region's own `onScroll` both correct them,
       and the buttons stay correct regardless because the browser clamps `scrollLeft` itself. */
  }, [compView, comps.length]);

  /* `scrollLeft = n` rather than `scrollBy`/`scrollTo`: the property assignment is the one that
   * exists everywhere, including jsdom, where the methods throw "Not implemented" and would take
   * a test down with them. One step is ~a third of the visible width, so the columns either side
   * of the jump overlap and the eye keeps its place. */
  const scrollComps = (dir: -1 | 1) => {
    const el = compScrollRef.current;
    if (!el) return;
    const step = Math.max(240, Math.round(el.clientWidth / 3));
    el.scrollLeft = Math.min(Math.max(0, el.scrollLeft + dir * step), el.scrollWidth - el.clientWidth);
    setCompScroll({ left: el.scrollLeft, max: Math.max(0, el.scrollWidth - el.clientWidth) });
  };

  /** Purely presentational disclosure state — which of the two "you already answered this" blocks
   * the agent has opened. Deliberately NOT lifted: nothing here is a fact about the property, and
   * every piece of DATA this component renders still lives in the parent (see the docstring). */
  const [quietOpen, setQuietOpen] = useState(false);
  const [conditionOpen, setConditionOpen] = useState(false);
  const [optionalOpen, setOptionalOpen] = useState(false);
  /** "More details (n filled)": optional and display-only rows, closed until asked for. */
  const moreStoreKey = briefKey ? `vb-subject-more:${briefKey}` : null;
  const [moreOpen, setMoreOpenState] = useState<boolean>(() => {
    if (!moreStoreKey) return false;
    try { return window.sessionStorage.getItem(moreStoreKey) === "1"; } catch { return false; }
  });
  const setMoreOpen = (next: boolean) => {
    setMoreOpenState(next);
    if (!moreStoreKey) return;
    try { window.sessionStorage.setItem(moreStoreKey, next ? "1" : "0"); } catch { /* storage blocked: stays per-render */ }
  };
  /** Which field's circled-i explanation is open (one at a time; Escape closes it). */
  const [infoOpen, setInfoOpen] = useState<string | null>(null);

  /* HOW THE COMP TABLE IS ORDERED AND HOW WIDE ITS COLUMNS ARE (2026-08-10, owner). Both are
     presentational, both live here beside the other view state, and both are deliberately
     SESSION-ONLY — held in React state and nowhere else. No localStorage, no sessionStorage: this
     app runs in environments where neither exists, and a preference that throws on write is worse
     than one that resets.

     `compSort` of null means "the order the server ranked them in", which is a real answer and the
     one the screen starts on: `amb_report.py` sorts by the rich review ranking and pre-ticks the
     top set, so re-sorting on arrival would hide the recommendation the agent is being asked to
     confirm. Sorting is something they DO, not something that has already happened to them. */
  const [compSort, setCompSort] = useState<{ key: CompSortKey; dir: "asc" | "desc" } | null>(null);
  /* BEST MATCH IS THE DEFAULT WHERE IT IS OFFERED (C1, 2026-11-02). `arrival` is the agent choosing
     "Nearest first, then county records" instead. A best order that does not cover exactly the rows
     on screen (a sale was just added and the re-rank has not answered) is not used: the rows show in
     arrival order for that moment rather than in an order computed for a different set. */
  const [arrival, setArrival] = useState(false);
  const [whyOrderOpen, setWhyOrderOpen] = useState(false);
  const bestOffered = bestOrder !== undefined;
  const bestUsable = isPermutation(bestOrder, comps.length);
  const bestActive = bestOffered && !arrival && !compSort && bestUsable;

  /* HOW FAR ALONG THE COLUMNS THE AGENT IS  (2026-09-01, W2-UIFIX).
   *
   * WHY THIS IS STATE AND NOT A SCROLLBAR. The eighteen declared widths total 2062px and a 1920
   * viewport gives this page 1843, so 219px of columns start off-screen — and the region has
   * scrolled since 2026-08-29. Measured in the harness's own Chromium, and true of macOS by
   * default, the bar that would say so reserves nothing and paints nothing at rest:
   *
   *     .vb-comp-scroll  scrollWidth 2062  clientWidth 1843  offsetHeight 449  clientHeight 449
   *
   * `offsetHeight - clientHeight == 0` with 219px of overflow is an OVERLAY scrollbar. Styling
   * `::-webkit-scrollbar` did not dislodge it in this build, so the honest conclusion is that the
   * OS scrollbar cannot be relied on to carry this: on the owner's own machine there is nothing
   * to see until something is already moving, which is exactly the screenshot he keeps sending.
   *
   * So the affordance is the product's, not the platform's: two real buttons with real labels,
   * their disabled state driven by these numbers, and a fade at whichever edge still has columns
   * behind it. The scrollbar is still there and still works; nothing here replaces it. */
  const compScrollRef = useRef<HTMLDivElement | null>(null);
  const [compScroll, setCompScroll] = useState({ left: 0, max: 0 });
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  /** The in-flight column drag, and the teardown for its window listeners. A ref, not state: a
   * pointer moving across the screen must not re-render the table on every sample. */
  const colDrag = useRef<{ key: string; startX: number; startWidth: number } | null>(null);
  const endDrag = useRef<(() => void) | null>(null);
  useEffect(() => () => endDrag.current?.(), []);

  /** THE RENDER ORDER, AS INDICES INTO `comps` — never a re-ordered copy of the array itself.
   *
   * This is the whole safety of the feature. Every row's checkbox, every editable cell and the
   * auto-reselect rule all address a comp by its position in the `comps` prop, and the payload
   * builder in `CmaAmbFlow` walks that same array to assemble `comps` AND the parallel
   * `meta._overrides.comps` index list. Sorting the array would silently re-point all of it: the
   * tick the agent put on one sale would land on another, and an override snapshot would be
   * attributed to the wrong property. Sorting a list of INDICES leaves the underlying array — and
   * therefore every piece of state attached to it and every index anything else derives from it —
   * byte-identical. What changes is the order rows are painted in, and nothing else. */
  const compOrder = useMemo(() => {
    const order = comps.map((_, i) => i);
    /* THE LADDER LEADS, WHATEVER THE COLUMN SORT SAYS (2026-09-08). An agent who has named a
       primary comparable and then sorts by sale date has not un-named it; the three sales they
       spoke for stay at the front and the sort does what it always did to everything else.
       `displayOrder` is the SAME function `CmaAmbFlow` builds the submitted array with, which is
       what makes the printed order and the recorded order provably the same order. */
    if (!compSort) return displayOrder(comps, bestActive ? (bestOrder as number[]) : order);
    const dir = compSort.dir === "asc" ? 1 : -1;
    order.sort((a, b) => {
      const va = compSortValue(comps[a], compSort.key);
      const vb = compSortValue(comps[b], compSort.key);
      // A blank sinks in BOTH directions (see `compSortValue`), so it is compared before the
      // direction multiplier is applied rather than after it.
      if (va === null && vb === null) return a - b;
      if (va === null) return 1;
      if (vb === null) return -1;
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return a - b;   // a stable tie-break on the file's own order, so equal rows never shuffle
    });
    return displayOrder(comps, order);
  }, [comps, compSort, bestActive, bestOrder]);

  // 1G: the map numbers its pins by this order, so it is reported whenever it changes.
  const onOrderRef = useRef(compFocus?.onOrder);
  onOrderRef.current = compFocus?.onOrder;
  const orderKey = compOrder.join(",");
  useEffect(() => { onOrderRef.current?.(compOrder); }, [orderKey]); // eslint-disable-line react-hooks/exhaustive-deps


  /** Put one sale on the ladder, or take it off. The rules that keep the ladder contiguous — the
   *  swap, the promote-on-clear — are `compLadder`'s, not this component's. */
  function setLeadAt(i: number, position: LeadPosition | null) {
    onCompsChange(setLead(comps, i, position));
  }

  /**
   * ONE EDIT PATH FOR BOTH VIEWS (2026-08-29).
   *
   * This was defined inside the table's row map, closed over that row. The card surface needs the
   * same behaviour for the one case where it edits — a sale blocked on a missing required figure —
   * and two spellings of an override rule that writes `_raw` and can re-select a row is two places
   * for the audit trail to drift. It is the same function, unchanged, taking the index it used to
   * close over.
   */
  function editCompAt(i: number, patch: Partial<CandidateComp>) {
    const c = comps[i];
    if (!c) return;
    const isProviderCandidate = isProviderRow(c, rungLabels);
  const next = comps.slice();
  const raw = { ...(c._raw ?? {}) };
  // THE SNAPSHOT IS DELIBERATE EVEN WHEN THE ORIGINAL WAS NULL. Recording `_raw.x
  // = null` is what tells the audit trail (and `meta._overrides.comps`) that this
  // figure came from the human and not from the file — which is MORE important on
  // a field the file left blank, not less. What was wrong was the display: a null
  // original rendered as "As pulled: — corrected by you", i.e. the product
  // asserting the agent overrode a number that never existed. `CompNumInput` now
  // words that case as "not in your file — you supplied this". Provenance kept,
  // claim corrected.
  //
  // PROVENANCE KEYS ARE NOT FIGURES AND ARE NEVER SNAPSHOTTED. `_raw` answers one
  // question — "what did the file say this number was" — and it travels to the
  // server as `meta._overrides.comps`. A concession edit now also writes
  // `seller_concession_source` and `concession_status`, which are statements ABOUT
  // the figure rather than the figure; recording their old values here would put
  // "as pulled: source" into an override trail that is supposed to hold dollars.
  for (const key of Object.keys(patch) as (keyof CandidateComp)[]) {
    if (COMP_PROVENANCE_KEYS.has(key)) continue;
    if (!(key in raw) && c[key] !== patch[key]) {
      (raw as Record<string, unknown>)[key] = c[key];
    }
  }
  const updated: CandidateComp = { ...c, ...patch, _raw: raw };

  // RESTORE THE DEFAULT SELECTION WHEN THE MISSING PRICE ARRIVES (2026-08-01).
  //
  // A closed sale that reached the grid with `price_needs_input` was never
  // pre-selected for one reason and one reason only: the file didn't print its
  // sold price, so the server's ranking had nothing to rank it on. Typing that
  // number removes the sole obstacle — it should put the row back where rank
  // would have put it, not demand a second, undiscoverable click on a checkbox
  // that only just became clickable. Leaving it unticked is what produced the
  // live dead-end: the agent types the last price, the "waiting on a sold price"
  // explanation disappears because nothing is waiting any more, and Generate
  // greys out with nothing on screen saying why.
  //
  // Three deliberate limits, because "helpfully ticking boxes" is exactly how an
  // eight-comp upload silently prices off all eight:
  //   1. ONLY a row that arrived flagged `price_needs_input`. A comp the server
  //      ranked out of the top set for some other reason is not swept back in
  //      because someone corrected its price.
  //   2. NEVER a Cotality-sourced candidate. That rule is load-bearing — the
  //      server refuses an unreviewed provider comp outright — and it stays.
  //   3. STOPS AT THE FLOOR. Once enough usable comps are selected to build with,
  //      further prices are just data; which of them to price off is the agent's
  //      call, not this function's.
  //
  // GENERALIZED TO ANY REQUIRED FIELD (2026-08-04). The rule was written for a
  // missing price; a missing lot size or garage count is the same situation and
  // has the same right answer. Limit 1 widens accordingly — the row must have
  // ARRIVED short a required field (`price_needs_input` from the old path, or
  // `needs_fields` from the new one), so a comp the server ranked out for some
  // other reason is still never swept back in. Limits 2 and 3 are untouched.
  const wasBlocked = compMissingFields(c as unknown as Record<string, unknown>).length > 0;
  const nowComplete =
    compMissingFields(updated as unknown as Record<string, unknown>).length === 0;
  const arrivedShort =
    c.price_needs_input === true ||
    (Array.isArray(c.needs_fields) && c.needs_fields.length > 0);
  const selectedUsable = comps.filter(
    (x) =>
      !!x._selected &&
      compMissingFields(x as unknown as Record<string, unknown>).length === 0
  ).length;
  if (
    wasBlocked &&
    nowComplete &&
    !updated._selected &&
    arrivedShort &&
    !isProviderCandidate &&
    selectedUsable < floor
  ) {
    updated._selected = true;
    updated.confirmed = true;
  }

  next[i] = updated;
  onCompsChange(next);
}

  function toggleCompSort(key: CompSortKey) {
    setCompSort((prev) =>
      prev && prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: defaultSortDir(key) });
  }

  /** Every column's width AS CURRENTLY PAINTED, read off the header row.
   *
   * Taken the moment a resize starts, because the table is laid out automatically until then: the
   * first drag switches it to a fixed layout, and without seeding every column from what was on
   * screen a fixed layout would redistribute the untouched columns into equal shares and the whole
   * table would jump under the agent's hand. Zero-width measurements are ignored so a headless
   * environment (jsdom measures everything as zero) falls back to the declared widths instead of
   * collapsing the table. */
  function measuredWidths(from: HTMLElement | null): Record<string, number> {
    const row = from?.closest("tr");
    if (!row) return {};
    const out: Record<string, number> = {};
    Array.from(row.children).forEach((cell, i) => {
      const col = COMP_COLUMNS[i];
      const w = (cell as HTMLElement).getBoundingClientRect?.().width ?? 0;
      if (col && w > 0) out[col.key] = Math.round(w);
    });
    return out;
  }

  /** The width a column should count from, whether or not the browser has ever laid it out. */
  function baseWidth(col: CompColumn, seeded: Record<string, number>): number {
    return seeded[col.key] ?? colWidths[col.key] ?? col.width;
  }

  function resizeColumn(col: CompColumn, from: HTMLElement | null, next: (base: number) => number) {
    const seeded = { ...measuredWidths(from), ...colWidths };
    setColWidths({ ...seeded, [col.key]: Math.max(MIN_COL_WIDTH, Math.round(next(baseWidth(col, seeded)))) });
  }

  function beginColumnDrag(e: React.PointerEvent<HTMLElement>, col: CompColumn) {
    // The handle sits inside the header cell, which is also the sort control: without this the
    // same gesture that widens a column would also re-order the table.
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.closest("th") as HTMLElement | null;
    const seeded = { ...measuredWidths(th), ...colWidths };
    colDrag.current = { key: col.key, startX: e.clientX, startWidth: baseWidth(col, seeded) };
    setColWidths(seeded);
    const move = (ev: PointerEvent | MouseEvent) => {
      const d = colDrag.current;
      if (!d) return;
      const w = Math.max(MIN_COL_WIDTH, Math.round(d.startWidth + (ev.clientX - d.startX)));
      setColWidths((prev) => ({ ...prev, [d.key]: w }));
    };
    const up = () => endDrag.current?.();
    endDrag.current = () => {
      colDrag.current = null;
      window.removeEventListener("pointermove", move as EventListener);
      window.removeEventListener("pointerup", up);
      endDrag.current = null;
    };
    window.addEventListener("pointermove", move as EventListener);
    window.addEventListener("pointerup", up);
  }

  // Owner decision 6: two fields are carried and never shown -- see `NEVER_DISPLAYED_KEYS`.
  const hidden = new Set([...(hideKeys ?? []), ...NEVER_DISPLAYED_KEYS]);
  const quiet = new Set((quietKeys ?? []).filter((k) => !hidden.has(k)));
  const quietDefs = SUBJECT_FIELD_SCHEMA.filter((f) => quiet.has(f.key));
  /** A REQUIRED key can never be moved out of the active list, whatever the caller passes. This is
   * the component-side half of the promise in `optionalKeys`' docstring: no readiness gate is
   * weakened by this feature, by construction rather than by convention. */
  const optional = new Set(
    (optionalKeys ?? []).filter(
      (k) => !hidden.has(k) && !quiet.has(k)
        && !SUBJECT_FIELD_SCHEMA.some((f) => f.key === k && f.required)
    )
  );
  const optionalDefs = SUBJECT_FIELD_SCHEMA.filter((f) => optional.has(f.key));
  /** The rows tucked under "More details": optional/display-only, and not already placed in the
   * carried-over or nothing-on-record blocks. */
  /** A disagreement between acres and square feet is a build-stopping disclosure, so it is never
   * left behind a closed disclosure: while the acres row has something to say it stays up top. */
  const acresPromoted = !!acresConflictMessage(
    grid.acres?.value, isConfirmed(grid, "lot_sf") ? grid.lot_sf?.value : null);
  const isMore = (f: (typeof SUBJECT_FIELD_SCHEMA)[number]) => !!f.more && !(f.key === "acres" && acresPromoted);
  const moreDefs = SUBJECT_FIELD_SCHEMA.filter(
    (f) => isMore(f) && !hidden.has(f.key) && !quiet.has(f.key) && !optional.has(f.key));
  const moreFilled = moreDefs.filter((f) => {
    const v = grid[f.key]?.value;
    return v !== null && v !== undefined && v !== "";
  }).length;
  /** A required row that is no longer confirmed must never be collapsed out of sight — the gate
   * below will block the build on it and the agent has to be able to see why. */
  const quietBlocking = quietDefs.some((f) => f.required && !isConfirmed(grid, f.key));
  const quietExpanded = quietOpen || quietBlocking;
  /** Once a required row in here has needed attention, the block STAYS open until the agent closes
   * it themselves. Without the latch, re-ticking the row would collapse the block out from under
   * them at the exact moment they finished fixing it. */
  useEffect(() => {
    if (quietBlocking) setQuietOpen(true);
  }, [quietBlocking]);

  function setField(key: string, patch: Partial<SubjectGrid[string]>) {
    onChange({ ...grid, [key]: { ...grid[key], ...patch } });
  }

  /** Apply a human edit to a subject field, applying the override-provenance rule: the FIRST time
   * a field with a real (non-"You entered") source diverges from its as-pulled value, snapshot
   * that original value before overwriting it. Subsequent edits keep the SAME original snapshot
   * (never chain edit-over-edit), matching `CandidateComp._raw`'s identical rule on the comp side. */
  function editField(key: string, value: string | number | boolean | null) {
    const state = grid[key];
    const hadRealSource = !!state?.sourceTag && state.sourceTag !== "You entered";
    const alreadyEdited = !!state?.edited;
    const patch: Partial<SubjectGrid[string]> = { value, checked: true, sourceTag: state?.sourceTag ?? "You entered" };
    if (hadRealSource && !alreadyEdited) {
      patch.edited = true;
      patch.originalValue = state!.value;
    }
    setField(key, patch);
  }

  /** The agent picks one of the lot candidates. The row takes its value AND its source tag, and any
   * earlier edit is cleared: choosing a source is a fresh decision, not an edit of a figure. */
  function pickLot(index: number) {
    const c = grid.lot_sf?.candidates?.[index];
    if (!c) return;
    // A PICK NEVER ERASES AN EDIT (lead ruling, 2026-11-02; Editable-Data Invariant). If the agent
    // had corrected the figure, that edit moves into `supersededEdits` with both values and sources
    // and the new pick's, instead of vanishing. Earlier entries are carried forward.
    const cur = grid.lot_sf;
    const trail = [...(cur?.supersededEdits ?? [])];
    if (cur?.edited && cur.value != null) {
      trail.push({
        editedValue: cur.value, editedSource: "You entered",
        fromValue: cur.originalValue ?? null, fromSource: cur.sourceTag,
        supersededByValue: c.value_sqft, supersededBySource: c.source,
      });
    }
    setField("lot_sf", {
      supersededEdits: trail.length ? trail : undefined,
      value: c.value_sqft, sourceTag: c.source, checked: true, edited: false, originalValue: undefined,
      candidateIndex: index, recordId: c.record_id, computedNote: c.computed_note,
    });
  }

  /** The county's garage AREA, shown beside the garage dropdown and labeled for what it is. */
  const garageAreaNote = (() => {
    const v = grid.garage_sqft?.value;
    if (typeof v !== "number" || !Number.isFinite(v) || v <= 0) return null;
    return `County garage area: ${v.toLocaleString("en-US")} sq ft (not a car count)`;
  })();

  /** HOA is a multi-part answer. The composed string (or false, or blank) is the value; the parts
   * stay beside it. "Not sure" leaves the value blank and unchecked: it is never written as No. */
  function editHoa(detail: SubjectFieldDetail) {
    const state = grid.hoa;
    const value = composeHoa(detail);
    const blank = value === null;
    const hadRealSource = !!state?.sourceTag && state.sourceTag !== "You entered";
    const patch: Partial<SubjectGrid[string]> = {
      value, detail, checked: !blank, sourceTag: state?.sourceTag ?? "You entered",
    };
    if (hadRealSource && !state?.edited) {
      patch.edited = true;
      patch.originalValue = state!.value;
    }
    setField("hoa", patch);
  }

  /** ONE definition of a subject row, so a row can move between the "still to answer" list and the
   * quiet "already confirmed" block below without the checkbox, the source tag or the edited mark
   * drifting between two copies of the same markup. Lifted verbatim out of the grouped list. */
  /** The one-line, act-on-it-now complaint or control that belongs UNDER a particular row, or null
   * for the rows that need neither. Two rows have one:
   *
   *  - `acres`, which is checked against the confirmed square footage at the keystroke. See
   *    `subjectGrid.acresConflictMessage` for why this exists at all.
   *  - `half_baths`, which gets an explicit "None stated" control while it is blank, so a zero is
   *    an answer the agent GIVES rather than one anybody assumes. */
  function rowExtra(def: (typeof SUBJECT_FIELD_SCHEMA)[number]) {
    // THE COUNTY AND THE MLS DISAGREE ABOUT THE LOT (2026-11-03): both shown, the agent picks.
    if (def.key === "lot_sf" && grid.lot_sf?.candidates && grid.lot_sf.candidates.length >= 2) {
      const lot = grid.lot_sf;
      return (
        <>
          <LotConflictPicker
            candidates={lot.candidates!}
            selected={lot.checked && !lot.edited && lot.candidateIndex != null ? lot.candidateIndex : null}
            onPick={pickLot}
          />
          {(lot.supersededEdits ?? []).map((e, i) => (
            <p key={i} data-lot-superseded={i}
               style={{ margin: "2px 0 0 32px", fontSize: 12, lineHeight: 1.5, color: colors.textSecondary }}>
              Your earlier figure of {Number(e.editedValue).toLocaleString("en-US")} sq ft (You entered, from{" "}
              {e.fromSource ?? "the source"}{e.fromValue != null ? ` ${Number(e.fromValue).toLocaleString("en-US")} sq ft` : ""})
              was replaced by your pick of {e.supersededByValue.toLocaleString("en-US")} sq ft ({e.supersededBySource}).
              It stays in the record of your changes.
            </p>
          ))}
        </>
      );
    }
    if (def.key === "acres") {
      const msg = acresConflictMessage(grid.acres?.value, isConfirmed(grid, "lot_sf") ? grid.lot_sf?.value : null);
      if (!msg) return null;
      return (
        <p role="alert" style={{ margin: "2px 0 0 32px", fontSize: 12, lineHeight: 1.5, color: colors.caution }}>
          {msg}
        </p>
      );
    }
    // Only while the row is BLANK (2026-10-19). A county reading awaiting review (Collin "3.5" ->
    // 1 half) is not blank, and "None stated" beside it would overwrite the reading with a zero.
    const halfValue = grid.half_baths?.value;
    if (def.key === "half_baths" && !isConfirmed(grid, "half_baths")
        && (halfValue === null || halfValue === undefined || halfValue === "")) {
      // A BLANK HALF-BATH IS NEVER READ AS ZERO — not by the engine, not here (2026-08-06). The row
      // rendered empty beneath a Full baths row reading 3 and the agent typed 3 into it, and the
      // client's report said "3 full / 3 half". The gap is real and the fix is not to guess it: it
      // is to make "none" a single click, attributed to the person who clicked it.
      return (
        <div style={{ margin: "2px 0 0 32px", display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => editField("half_baths", 0)}
            style={{ padding: "5px 10px", borderRadius: 7, border: `1px solid ${colors.border}`,
                     background: colors.bgSurface, color: colors.accent, fontSize: 12, cursor: "pointer" }}
          >
            None stated — this home has no half bath
          </button>
          {/* THE WHOLE CUE, IN SEVEN WORDS (2026-08-12). This is now the only half-bath cue on the
              screen — the paragraph that used to restate it under the grid is gone (see `bathNote`).
              It sits beside the control that answers it, which is where a cue belongs. */}
          <span style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>
            A blank half bath is never read as zero.
          </span>
        </div>
      );
    }
    return null;
  }

  function renderRow(def: (typeof SUBJECT_FIELD_SCHEMA)[number]) {
    const state = grid[def.key] ?? { key: def.key, value: null, sourceTag: null, checked: false };
    const missingRequired = def.required && !state.checked;
    const pickedState = pickState(cadPick, def.key, state);
    // A READING THE AGENT MUST CONFIRM (owner, 2026-10-19): a Collin bath split, a Dallas
    // subdivision read from the legal description. Prefilled, unchecked, not priced until checked;
    // the chip says so beside the value and goes once the row is checked or edited.
    const reviewing = awaitingReview(cadPick, def.key, state);
    const stateNote = pickedState?.note ? (
      <p data-state-note={def.key}
         style={{ margin: "2px 0 0 32px", fontSize: 12, lineHeight: 1.5, color: colors.textSecondary }}>
        {pickedState.note}
      </p>
    ) : null;
    const ruleExtra = rowExtra(def);
    const infoPanel = def.explain && infoOpen === def.key ? (
      <p id={`vb-info-${def.key}`} data-info-panel={def.key} role="note"
         style={{ margin: "2px 0 0 32px", fontSize: 12, lineHeight: 1.5, color: colors.textSecondary }}>
        {def.explain}
      </p>
    ) : null;
    const extra = ruleExtra || stateNote || infoPanel ? <>{infoPanel}{stateNote}{ruleExtra}</> : null;
    const row = (
      <div
        data-field-row={def.key}
        className="vb-subject-row"
        style={{
          display: "grid",
          /* THE ROW HAS A RIGHT EDGE (2026-08-29). The source column used to be `1.2fr` with its
             text left-aligned, so on a 1200px page every row ended with ~286px of empty card to
             the right of a ~100px tag — twenty-two rows of ragged right edge, which is a large part
             of what the owner was looking at when he said the blocks and fields are all way off.
             `auto` sizes the column to the tag it actually holds and `justifyItems` below pins it
             to the right, so the provenance reads as a column and the card closes cleanly.

             The value column is capped rather than fluid for the same reason. At `1fr` a four-digit
             square-footage sat in a 428px box with 600px of empty card between it and its own
             label; capped at 260 it sits directly beside the provenance it came from, and the label
             column absorbs the slack. Label left, value and source right: three edges, not one edge
             and two drifts. */
          /* THE VALUE COLUMN'S CEILING CAME DOWN WITH THE COLUMN COUNT (2026-08-30). 260 was
             derived when this row had a full 1200px card to itself; inside a ~590px group card it
             takes 44% of the row and starves the label, which is where the multi-line hints live —
             "Square feet — the living-area adjustment divides by this" was wrapping to three lines
             and making a one-fact row 60px tall. The FLOOR stays 180 so a long figure still has its
             room; only the ceiling moves, so the wide single-column contexts that share this row
             lose nothing they were using. */
          /* THE LAST TRACK COULD NOT SHRINK, AND THAT WAS THE ONLY SIDEWAYS SCROLL IN THE APP
             (2026-08-30). Measured at 390: `document.scrollWidth` 406 against a 390 viewport —
             eight identical 102x17 source-tag spans, `white-space: nowrap`, ending at x=406. An
             `auto` track takes its content's max-content width and a nowrap span has no smaller
             one, so the row could never fit a phone. `minmax(0, auto)` lets it shrink; the tag
             below is allowed to wrap rather than push the page. Steps 2-5 were already clean, so
             this row is the whole of the defect. */
          gridTemplateColumns: "22px minmax(0, 1fr) minmax(180px, 200px) minmax(0, auto)",
          alignItems: "center",
          gap: 10,
          padding: "7px 12px",
          background: colors.bgSurface,
          border: `1px solid ${missingRequired ? colors.caution : colors.border}`,
          borderRadius: 8,
        }}
      >
        <input
          type="checkbox"
          checked={state.checked}
          aria-label={`Include ${def.label} in the subject`}
          onChange={(e) => setField(def.key, { checked: e.target.checked })}
        />
        <div style={{ fontSize: 13, color: colors.textSecondary, minWidth: 0 }}>
          <label>
            {def.label}
            {def.required && <span style={{ color: colors.caution }}> *</span>}
          </label>
          {def.explain && (
            <InfoButton label={def.label} open={infoOpen === def.key} panelId={`vb-info-${def.key}`}
                        onToggle={() => setInfoOpen((k) => (k === def.key ? null : def.key))} />
          )}
          {def.hint && <div style={{ fontSize: 11, color: colors.textSecondary }}>{def.hint}</div>}
        </div>
        <FieldInput def={def} state={state} edited={!!state.edited}
                    digits={def.key === "acres" && pickedState?.state === "computed" ? COMPUTED_DIGITS : undefined}
                    garageAreaNote={def.key === "garage" ? garageAreaNote : null}
                    onDetail={def.key === "hoa" ? editHoa : undefined}
                    onChange={(value) => editField(def.key, value)} />
        {/* "NOT IN YOUR FILE" ONLY WHEN THERE WAS A FILE (2026-08-11). This is the empty-source
            column, and since the source cascade an agent can be looking at this form having
            uploaded nothing — the subject came from the public record off a typed address. Every
            unfilled row was then telling them their file was missing something they never sent. The
            fact is the same either way and does not need to name a source to state it. */}
        {/* THE SOURCE COLUMN HAS A CEILING (2026-10-19, #40 x #35, measured in Chromium at 1440).
            Its track is `auto`, so a long sentence (a county's why-blank note, a full CAD tag) took
            its whole width on one line and starved the label to one word per line; with #35's
            bordered blank cell the label's words then ran under the "Enter" box. Capping the span
            caps the track, and the sentence wraps instead. */}
        <span style={{ fontSize: 12, color: colors.textSecondary, fontStyle: state.sourceTag ? "normal" : "italic",
                       textAlign: "right", justifySelf: "end", minWidth: 0, maxWidth: SOURCE_MAX_W }}>
          {/* AN EDITED FIGURE IS THE AGENT'S (2026-10-04, owner: "any field the agent then edits
              becomes 'You entered'"). The submit already sends it as "You entered"
              (`subject_field_sources`) with the original in `_overrides`; the row used to keep
              printing the source's name beside a number the source never stated. The origin of
              record is not lost: it is named, with the as-pulled value, on the "edited" mark. */}
          {state.edited ? "You entered" : (state.sourceTag ?? (def.key === "lot_sf" && state.candidates && state.candidates.length >= 2 ? LOT_UNRESOLVED_WORDS : def.key === "hoa" && state.detail?.answer === "not_sure" ? "Not sure" : (blankNote?.(def.key) ?? def.blankWords ?? "Nothing on record")))}
          {def.key === "lot_sf" && state.recordId && !state.edited && state.sourceTag && (
            <span data-lot-record style={{ marginLeft: 6, fontStyle: "normal" }}>
              · record {state.recordId}
            </span>
          )}
          {def.key === "lot_sf" && state.computedNote && !state.edited && state.sourceTag && (
            <span data-lot-computed-note style={{ display: "block", fontStyle: "normal", fontSize: 11 }}>
              Computed: {state.computedNote}
            </span>
          )}
          {state.edited && (
            <span
              title={editedTitle(state)}
              style={{ marginLeft: 6, color: colors.caution, fontStyle: "normal", fontWeight: 600 }}
            >
              · edited
            </span>
          )}
          {pickedState && (
            <span data-field-state={def.key}
                  style={{ marginLeft: 6, fontStyle: "normal", fontWeight: 600, color: colors.textSecondary }}>
              · {pickedState.state}
            </span>
          )}
          {reviewing && (
            <span data-needs-review={def.key}
                  style={{ display: "inline-block", marginLeft: 6, padding: "0 6px", borderRadius: 6,
                           fontStyle: "normal", fontWeight: 600, fontSize: 11, lineHeight: "18px",
                           color: colors.amber, background: colors.amberBg,
                           border: `1px solid ${colors.amberLine}` }}>
              Needs your review
            </span>
          )}
          {/* "History" — owner spec 2026-10-04 §2. Only for a row the CAD record actually tagged
              (every CAD tag starts with the record's own `cadLabel` — see `cadPick.ts`), and only
              while it still carries that tag: once the agent edits it away to "You entered" there
              is no CAD field left to ask the history route about. Renders nothing itself when
              there is no `cadPick` (the ordinary, non-CAD subject) — see the component's own note
              on why that is a disappearing control, not a disabled one. */}
          {cadPick && !state.edited && !!state.sourceTag?.startsWith(cadPick.cadLabel) && (
            <FieldHistoryDisclosure
              county={cadPick.county}
              propId={cadPick.propId}
              field={def.key}
              fieldLabel={def.label}
            />
          )}
        </span>
      </div>
    );
    if (!extra) return <div key={def.key}>{row}</div>;
    return (
      <div key={def.key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {row}
        {extra}
      </div>
    );
  }

  /* THE BATHROOM ADJUSTMENT IS OPT-IN BY DATA, AND NOW SAYS SO (2026-08-04).

     `engine.amb._lines` prices full and half baths only when the subject is `baths_known`, and
     that flag is only set when BOTH counts are confirmed — because a confirmed 3 full with the
     half row blank does not mean zero half baths, it means nobody said, and an unknown bath must
     never become a zero-difference adjustment. The consequence was invisible: leave either row
     blank and the subject's extra bathroom is worth $0 with no line, no set-aside and no
     disclosure.

     RENDERED INSIDE THE GROUP THAT HOLDS THE BATH ROWS. It first went in just above the condition
     control with a negative top margin to tuck it up — which painted it straight over the
     condition heading and its select, making the dropdown hard to click. A note about two fields
     belongs under those two fields, in normal flow, where it cannot collide with anything.

     WHEN THE BATH ROWS ARE QUIET (2026-08-05) they are not re-presented on this screen, so the
     note has no rows to sit under and is rendered once below the active groups instead. It is a
     live statement about what will and will not be priced, and it stays on screen either way. */
  /* ⚠️ THE NOTE THIS REPLACES WAS TRUE OF THE ENGINE AND IS NO LONGER (2026-08-07, owner: "we know
     the subject has 3, some of the comps have 2, why did this happen ... what is happening in the
     gates with this").

     It read "Bathrooms will NOT be priced until BOTH the full and half bath counts are confirmed."
     That was an accurate description of `engine.amb._lines`, which consulted the two counts as a
     PAIR — so a blank HALF row suppressed the FULL row as well, and a three-full-bath home against
     two-full-bath sales was adjusted by $0. The gate is per line now
     (`engine.amb.bath_line_priceable`): a blank half count is silence about half baths and about
     nothing else, and a full-bath difference between two stated numbers is priced.

     So this note now has THREE states, not two, because the middle one is the ordinary case and it
     is the one the old copy got wrong. It must also stop implying that the half row is optional
     housekeeping: the review screen collapses unconfirmed optional fields under "the report does
     not need them", which is exactly where the agent could not find the row that was costing them
     an adjustment. When the full count is confirmed and the half is not, this says what is being
     priced, what is not, and that 0 is a real answer. */
  /* \u26a0\ufe0f THE MIDDLE STATE NO LONGER SPEAKS AS A PARAGRAPH (2026-08-12, owner, twice: "this is
     noise"). With the full count confirmed and the half row blank, this rendered three sentences
     explaining that a blank is not a zero and inviting the agent to press the button \u2014 directly
     underneath THAT BUTTON, which says "None stated \u2014 this home has no half bath" and carries its
     own one-line cue. A lecture attached to a control that already says what it does is not a
     disclosure, it is something to scroll past, and it made the two states that DO need a sentence
     (nothing confirmed; both confirmed) easier to ignore.

     THE REQUIREMENT DID NOT CHANGE, ONLY WHERE IT IS ENFORCED. "A blank half bath is never read as
     zero" is still stated on this screen \u2014 as the phrase beside the None-stated control in
     `rowExtra` \u2014 and the DELIVERABLE still carries it in full: one sentence under the adjustment
     grid (`recon_disclosures` extra) plus the em-dash cells that show which sales never stated a
     half bath. Nothing was removed from the report. */
  const bathNote = (() => {
    const full = grid.full_baths?.checked ? grid.full_baths.value : null;
    const half = grid.half_baths?.checked ? grid.half_baths.value : null;
    const fullKnown = typeof full === "number";
    const halfKnown = typeof half === "number";
    const body = fullKnown && halfKnown
      ? "Bathrooms will be priced: both the full and half counts are confirmed, so each comparable is adjusted for the difference against your home."
      : fullKnown
      ? null
      : "Bathrooms will NOT be priced until a bath count is confirmed \u2014 a blank row means \u201cnobody said\u201d, not \u201czero\u201d, and we never treat an unknown bathroom as no difference. Enter the full count to have that difference adjusted on every comparable.";
    if (!body) return null;
    return (
      <p style={{ marginTop: 10, marginBottom: 0, fontSize: 12,
                  color: fullKnown && halfKnown ? colors.textSecondary : colors.caution,
                  lineHeight: 1.5 }}>
        {body}
      </p>
    );
  })();

  /** Whether the `full_baths` row is still rendered as a question here. It is the exact complement
   * of the in-group condition below (the note is drawn under the group that still holds that row),
   * so the note is rendered exactly once whichever way the rows are split. */
  const fullBathsRowActive =
    !hidden.has("full_baths") && !quiet.has("full_baths") && !optional.has("full_baths");

  /* A BIGGER TARGET, AND ROOM TO WRAP (2026-08-04). The select sat flush against its help text in
     a non-wrapping flex row, so on a narrow viewport the two crowded each other and the dropdown
     became fiddly to hit. It carries the largest judgment on the screen; it should not be the
     hardest control to click.

     Held in a variable (2026-08-05) so the "confirm" mode below reveals THE SAME control rather
     than a second copy of it. */
  const conditionControl = (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", rowGap: 8 }}>
        <select
          /* Same guard as the per-comp select below. The subject's state is seeded "average" so
             this is belt-and-braces today, but a draft saved before the ladder existed, or any
             future path that seeds the subject from a provider, would hit the identical
             display-the-first-option trap. One rule, both selects. */
          value={normalizeCondition(condition)}
          onChange={(e) => onConditionChange(e.target.value)}
          aria-label="Condition of this home"
          style={{ padding: "9px 12px", borderRadius: 6, border: `1px solid ${colors.border}`,
                   fontSize: 13, minWidth: 170, background: colors.bgSurface,
                   color: colors.textPrimary, cursor: "pointer" }}
        >
          {CONDITIONS.map((c) => (
            <option key={c} value={c}>
              {CONDITION_LABELS[c]}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: colors.textSecondary, flex: "1 1 320px", lineHeight: 1.45 }}>
          Defaults to Average — raise after a walk-through. Never set from a data source.
        </span>
      </div>
      {/* SAY WHAT THIS CONTROL IS WORTH (2026-08-04).
          It sat here as an unremarkable dropdown with a quiet default, and it is the largest
          single judgment on the screen: `engine.amb` moves every comparable by
          PARAMS["condition_step"] (5%) of ITS OWN sale price for each step between that comp's
          condition and the subject's. On a ~$400,000 street that is about $20,000 on the
          reconciled point, per step.

          Measured on the owner's own Springtree file, changing nothing else: subject on
          Average reconciles to $413,000 and subject on Updated to $433,000. The owner's report
          was "the number looks low" — and the cause was a control left on its default because
          nothing on screen said it mattered.

          STATING THE SIZE OF A LEVER IS NOT PULLING IT. Nothing here changes the default,
          computes an adjustment, or hints at a direction — the judgment stays the agent's under
          VI-3, which is precisely why they have to know what it is worth. The figure is derived
          from the comps actually on screen rather than hardcoded, so it cannot go stale against
          a different price band. */}
      <p style={{ ...blockStyle("copy"), marginTop: 8, marginBottom: 0, fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>
        This is the biggest single judgment on this screen. Each step moves every comparable by
        about 5% of its own sale price{conditionStepHint(comps)}. A home left on Average that you
        would call Updated after walking it reads about that much low.
      </p>
    </>
  );

  /* WHERE THE CONDITION CARD SITS IN THE FLOW. After Size / structure, not last, and the reason is
     editorial before it is arithmetic: those are the two blocks on this screen that MOVE THE
     NUMBER — the square footage and age the engine prices from, and the one judgment it is
     forbidden to make for you — while Features, Neighborhood and Construction describe the home.
     Putting the biggest judgment on the screen at the end of a run of descriptive cards was an
     accident of it having been a separate band; this is where it belongs.
     It also balances the masonry, which is the defect that sent me looking: measured on
     `step2-detail@1920`, the two columns ended 230px apart before this change set, and the card's
     placement is what decides how much of that is left. The fallback keeps it rendering exactly
     once if the schema ever renames that group. */
  /* Derived from the groups that will actually RENDER, not from the schema's list. On a restored
     draft every row of a group can be quiet, and that group returns null — so anchoring the card to
     a group by name alone would drop the condition control off the screen entirely on exactly the
     path where it is already collapsed to one line. `null` here means no group rendered at all, and
     the card is emitted after the flow instead. */
  const renderedGroups = groups.filter((g) =>
    SUBJECT_FIELD_SCHEMA.some(
      (f) => f.group === g && !isMore(f) && !hidden.has(f.key) && !quiet.has(f.key) && !optional.has(f.key),
    ),
  );
  const conditionAfter: string | null = renderedGroups.includes("Size / structure")
    ? "Size / structure"
    : (renderedGroups[renderedGroups.length - 1] ?? null);

  const conditionCard = (
    <>
  {/* CONDITION IS A SUBJECT ATTRIBUTE, SO IT SITS WITH THEM (2026-09-01, W3-UIFIX2).

      WHAT WAS WRONG, from `w2-uifix/after/step2-detail@1920.png`. Five cards flow into two
      masonry columns and none of them may be split (`break-inside: avoid`), so the
      balancer has to place whole boxes: Identity + Size/structure landed in the left
      column and Features + Neighborhood + Construction in the right, leaving ~230px of
      empty page under the left column while the right ran on. Below it, Condition rendered
      as a full-width band holding a 560px control and a 640px paragraph, with the entire
      right half of a 1920 viewport empty beside it. Two symptoms, one cause: there were
      five boxes to balance and a sixth one standing outside the flow.

      Condition belongs in that flow on the merits, not just arithmetically — it is the one
      attribute of this home the record may never set, which is exactly what the card says
      — and putting it there gives the balancer a sixth box to place, which is what closes
      the hole. DOM order moves it above the carried-over and optional blocks; reading
      order still goes attributes, then judgment, then what was already confirmed.

      Below 1180px `.vb-subject-groups` is one column and this is a card in a stack, which
      is what it was before. */}
  {conditionMode === "confirm" && (
    <section className="vb-subject-group">
      <h3 className="vb-subject-group-head">Condition (human judgment)</h3>
      <p style={{ margin: 0, fontSize: 13, color: colors.textPrimary, lineHeight: 1.5 }}>
        <strong>{CONDITION_LABELS[condition as Condition] ?? condition}</strong>
        <span style={{ color: colors.textSecondary }}>
          {" "}— your judgment, carried from the step before. Never set from a data source.
        </span>
      </p>
      <button
        type="button"
        aria-expanded={conditionOpen}
        onClick={() => setConditionOpen((o) => !o)}
        style={{ marginTop: 10, padding: "7px 12px", borderRadius: 8, border: `1px solid ${colors.border}`,
                 background: colors.bgSurface, color: colors.accent, fontSize: 13, cursor: "pointer" }}
      >
        {conditionOpen ? "Done — keep this condition" : "Change the condition"}
      </button>
      {conditionOpen && <div style={{ marginTop: 12 }}>{conditionControl}</div>}
    </section>
  )}
  {conditionMode === "ask" && (
    <section className="vb-subject-group">
      <h3 className="vb-subject-group-head">Condition (human judgment)</h3>
      {conditionControl}
    </section>
  )}
    </>
  );

  return (
    <div
      style={{ fontFamily: typeTokens.fontUi, color: colors.textPrimary }}
      onKeyDown={(e) => {
        if (e.key !== "Escape" || !infoOpen) return;
        const key = infoOpen;
        setInfoOpen(null);
        // Focus returns to the circled i that opened the note, so a keyboard user is not dropped.
        document.querySelector<HTMLElement>(`button[aria-controls="vb-info-${key}"]`)?.focus();
      }}
    >
      {/* FIVE CARDS IN TWO COLUMNS, NOT TWENTY-ONE ROWS IN ONE (2026-08-30, owner: "wide open
          screen real estate blank", and this is the screen where that was worst).

          WHAT WAS WRONG. Every group was a bare uppercase heading over a stack of full-width
          strips, and the whole screen was one column. Measured at 1512px: 2742px tall — the
          longest scroll in the wizard — with each row's label cell taking ALL the horizontal slack
          (`minmax(0, 1fr)`) while holding a two-word label, so a ~400px gap of nothing sat between
          "Bedrooms" and "4" on nineteen consecutive rows. The page was simultaneously too long and
          too empty, which is the same defect wearing two faces.

          WHY CARDS AND WHY TWO. The groups are already the right unit — Size / structure is the
          block that decides the valuation, Construction is reference — they just had no edges, so
          nothing separated one from the next but a change of capitalisation. Giving each an edge
          also gives the two-column grid something to align, and two columns halve the scroll AND
          starve the label cell of the slack it was wasting: at ~590px per card the label column
          settles at roughly its own text width and the value lands beside it.

          THE BREAKPOINT IS THE CARD, NOT THE SCREEN. 1180 because the `form` measure caps at 1200,
          and two cards plus the gap need ~590 each for the row's own
          `22px / label / minmax(180px, 260px) / auto` to fit without the hints wrapping every
          line. Below it, one column — exactly what this screen was, which is the right layout for
          a phone and was only ever wrong for a desktop.

          DOM ORDER IS UNCHANGED, so the reading order a screen reader follows and the order the
          tab key visits are the same as they were. This is a placement change and nothing else. */}
      {showSubject && (
        <div className="vb-subject-groups">
          {groups.map((group) => {
            const defs = SUBJECT_FIELD_SCHEMA.filter(
              (f) => f.group === group && !isMore(f) && !hidden.has(f.key) && !quiet.has(f.key) && !optional.has(f.key)
            );
            // A group whose every row was answered a step ago is not an empty heading to scroll past.
            if (!defs.length) return null;
            return (
                <Fragment key={group}>
                <section className="vb-subject-group">
                <h3 className="vb-subject-group-head">{group}</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {defs.map(renderRow)}
                </div>
                {showBathNote && defs.some((f) => f.key === "full_baths") && bathNote}
                </section>
                {group === conditionAfter && conditionCard}
                </Fragment>
            );
          })}
          {conditionAfter === null && conditionCard}
        </div>
      )}

      {/* MORE DETAILS (Q3, 2026-10-25). Optional and display-only rows (nothing the engine or the
          readiness gate reads) wait here, closed. Every one is still a real, editable, source-tagged
          row; the count says how many already hold a value so nothing looks absent. */}
      {showSubject && moreDefs.length > 0 && (
        <section aria-labelledby="vb-more-heading" style={{ marginBottom: 24 }}>
          <h3 id="vb-more-heading" style={{ margin: 0 }}>
            <button
              type="button"
              aria-expanded={moreOpen}
              aria-controls="vb-more-rows"
              onClick={() => setMoreOpen(!moreOpen)}
              style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${colors.border}`,
                       background: colors.bgSurface, color: colors.accent, fontSize: 13, cursor: "pointer" }}
            >
              {moreOpen ? "Hide" : "Show"} more details ({moreFilled} filled)
            </button>
          </h3>
          {moreOpen && (
            <div id="vb-more-rows" className="vb-subject-groups" style={{ marginTop: 12 }}>
              {Array.from(new Set(moreDefs.map((f) => f.group))).map((group) => (
                <section className="vb-subject-group" key={group}>
                  <h3 className="vb-subject-group-head">{group}</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {moreDefs.filter((f) => f.group === group).map(renderRow)}
                  </div>
                </section>
              ))}
            </div>
          )}
        </section>
      )}

      {/* The bath rows are already answered and quiet, so the note that says what that buys sits
          on its own rather than disappearing with them. */}
      {showSubject && showBathNote && !fullBathsRowActive && bathNote
        && <section style={{ marginBottom: 24 }}>{bathNote}</section>}

      {/* ALREADY CONFIRMED, ONE STEP EARLIER (2026-08-05, owner: "the tool makes us confirm the
          subject file multiple times"). Visible, checkable at a glance, and NOT presented as a
          fresh question. One control turns the whole block back into the same editable rows it
          would otherwise have been — the same `renderRow` above, not a lookalike. */}
      {showSubject && quietDefs.length > 0 && (
        <section
          aria-labelledby="vb-quiet-heading"
          style={{ marginBottom: 24, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 14, background: colors.bgSurface }}
        >
          <h3 id="vb-quiet-heading" className="vb-sec-label">
            {quietTitle ?? `Already confirmed · ${quietDefs.length}`}
          </h3>
          {quietBlurb && (
            <p style={{ margin: "6px 0 0", fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>
              {quietBlurb}
            </p>
          )}
          {quietBlocking && (
            <p role="alert" style={{ margin: "8px 0 0", fontSize: 12, color: colors.caution, lineHeight: 1.5 }}>
              One of these is a required detail and is no longer confirmed, so they stay open until
              it is. The report cannot compute without it.
            </p>
          )}

          {quietExpanded ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
              {quietDefs.map(renderRow)}
            </div>
          ) : (
            <dl style={{ margin: "10px 0 0", display: "grid",
                         gridTemplateColumns: "minmax(140px, 1fr) minmax(90px, 1fr) minmax(120px, 1.2fr)",
                         gap: "6px 12px", fontSize: 12 }}>
              {quietDefs.map((def) => {
                const state = grid[def.key];
                const quietState = pickState(cadPick, def.key, state);
                return (
                  <div key={def.key} style={{ display: "contents" }}>
                    <dt style={{ color: colors.textSecondary }}>{def.label}</dt>
                    <dd style={{ margin: 0, fontFamily: "monospace", color: colors.textPrimary }}>
                      {displayValue(state, def.key === "acres" && quietState?.state === "computed" ? COMPUTED_DIGITS : undefined)}
                    </dd>
                    <dd style={{ margin: 0, color: colors.textSecondary }}>
                      {state?.edited ? "You entered" : (state?.sourceTag ?? "Not stated")}
                      {state?.edited && (
                        <span title={editedTitle(state)} style={{ color: colors.caution, fontWeight: 600 }}> · edited</span>
                      )}
                      {quietState && (
                        <span data-field-state={def.key} style={{ fontWeight: 600 }}> · {quietState.state}</span>
                      )}
                      {quietState?.note && (
                        <span data-state-note={def.key} style={{ display: "block" }}>{quietState.note}</span>
                      )}
                    </dd>
                  </div>
                );
              })}
            </dl>
          )}

          {!quietBlocking && (
            <button
              type="button"
              aria-expanded={quietExpanded}
              onClick={() => setQuietOpen((o) => !o)}
              style={{ marginTop: 12, padding: "7px 12px", borderRadius: 8, border: `1px solid ${colors.border}`,
                       background: colors.bgSurface, color: colors.accent, fontSize: 13, cursor: "pointer" }}
            >
              {quietExpanded ? "Done — collapse these" : `Correct any of these ${quietDefs.length}`}
            </button>
          )}
        </section>
      )}

      {/* OPTIONAL, AND NOTHING ON RECORD — NOT OUTSTANDING WORK (2026-08-06). See `optionalKeys`.
          Collapsed, labelled for what it is, and one click from editable. The point of the block is
          the sentence, not the hiding: an agent who reads "we have nothing for these and the report
          does not need them" does not go looking for something to type. */}
      {showSubject && optionalDefs.length > 0 && (
        <section
          aria-labelledby="vb-optional-heading"
          style={{ marginBottom: 24, border: `1px dashed ${colors.border}`, borderRadius: 10, padding: 14 }}
        >
          <h3 id="vb-optional-heading" className="vb-sec-label">
            Optional · nothing on record · {optionalDefs.length}
          </h3>
          <p style={{ margin: "6px 0 0", fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>
            Nothing you have given us says anything about {optionalDefs.map((f) => f.label.toLowerCase()).join(", ")}.
            None of them is required and the report does not need them — leaving them blank costs
            nothing. Open this only if you know the answer from the record.
          </p>
          {optionalOpen && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
              {optionalDefs.map(renderRow)}
            </div>
          )}
          <button
            type="button"
            aria-expanded={optionalOpen}
            onClick={() => setOptionalOpen((o) => !o)}
            style={{ marginTop: 12, padding: "7px 12px", borderRadius: 8, border: `1px solid ${colors.border}`,
                     background: colors.bgSurface, color: colors.accent, fontSize: 13, cursor: "pointer" }}
          >
            {optionalOpen ? "Done — collapse these" : `Add any of these ${optionalDefs.length} if you know them`}
          </button>
        </section>
      )}


      {/* Comp review — each candidate confirmed individually before it can feed a build. */}
      {showComps && <section>
        {/* ONE TOOLBAR, NOT A HEADER STACK  (2026-08-31, owner, from a screenshot of this exact
            screen: "above the cards or table view, there is a lot of open space. everything is
            crowded left").

            THE MEASUREMENT, because the complaint has an arithmetic cause rather than a taste one.
            This screen renders at `content="data"`. At a 1920 viewport `measures.gutterAt` gives
            38.4px, so the page box is 1843px and the 2044 cap does not bind. Three separate things
            were laid out against that 1843px, and each was wrong in its own direction:

              1. THE HEADING ROW was `justify-content: space-between` across the whole 1843px, so
                 the view switch ended up roughly 1700px away from the heading it belongs to. A
                 control that far from its subject does not read as belonging to it; it reads as a
                 stranded object, which is what "isolated at the far right" describes.
              2. THE GUIDANCE PANEL was capped at `measure.copy` (640) and left-aligned, so it was a
                 640px column with 1200px of nothing beside it. That is the "open space" and the
                 "crowded left" in one element -- they were never two complaints.
              3. THE SORT CONTROL was a THIRD full-width band below both, and only in the cards
                 view, so the two views did not even have the same chrome height.

            Roughly 450px of vertical chrome before the first sale. The fix is the pattern every
            application-grade table uses and this screen was not using: ONE toolbar row that owns
            the title, the count and every control over the set, with the controls grouped at the
            right of the SAME row rather than at the right of the page.

            NOTHING IS CUT, and that matters more here than tidiness. The sentence that states what
            the set contains -- every sale, including the incomplete ones, nothing assumed -- is a
            claim about the honesty of the data and it stays on screen, always, at full size. Only
            the MECHANICAL instructions (which cells are editable, how to sort, how to drag a
            divider) move behind a disclosure, because they are how-to text an agent needs once and
            then never again. The 2026-08-30 note that grouped these four paragraphs into a panel
            was right that they must not be deleted; a closed disclosure does not delete them.

            THE SORT CONTROL NOW SHOWS IN BOTH VIEWS. It was cards-only because the table had
            clickable column headings, which is true and is also not a reason to make the same
            state reachable two different ways depending on which view you are in. */}
        <div className="vb-comp-bar">
          <div className="vb-comp-bar-title">
            <h3 className="vb-sec-label" style={{ margin: 0 }}>Comparable sales</h3>
            {/* The count is its own element rather than a parenthetical inside the heading: it
                changes on every tick, and a heading whose text changes is a heading a screen
                reader re-announces. `aria-live` says it once, quietly, when it moves. */}
            <span className="vb-comp-bar-count" aria-live="polite">
              {comps.filter((c) => c._selected).length} of {comps.length} selected
            </span>
          </div>
          <div className="vb-comp-bar-tools">
            <label className="vb-comp-sort">
              <span>Order by</span>
              <select
                value={compSort?.key ?? (bestOffered && !arrival ? "best" : "rank")}
                aria-describedby={compSort ? undefined : "vb-comp-sort-hint"}
                onChange={(e) => {
                  const v = e.target.value;
                  // "As found" is a real answer, not an absence: it is the order the server ranked
                  // them in, and it is what an agent should see before imposing one of their own.
                  if (v === "best") { setCompSort(null); setArrival(false); }
                  else if (v === "rank") { setCompSort(null); setArrival(true); }
                  else setCompSort({ key: v as CompSortKey, dir: defaultSortDir(v as CompSortKey) });
                }}
              >
                {bestOffered && <option value="best">{BEST_MATCH_LABEL}</option>}
                <option value="rank">{SEARCH_ORDER_LABEL}</option>
                {CARD_SORTS.map((k) => (
                  <option key={k} value={k}>
                    {COMP_COLUMNS.find((c) => c.sort === k)?.label ?? k}
                  </option>
                ))}
              </select>
            </label>
            {bestOffered && !compSort && !arrival && (
              <button type="button" className="vb-infotip" aria-label="Why this order"
                      aria-expanded={whyOrderOpen} aria-controls="vb-comp-why-order"
                      data-testid="best-match-why"
                      onClick={() => setWhyOrderOpen((o) => !o)}>
                i
              </button>
            )}
            {compSort && (
              <button
                type="button"
                className="vb-comp-more"
                onClick={() => setCompSort({ key: compSort.key, dir: compSort.dir === "asc" ? "desc" : "asc" })}
              >
                {compSort.dir === "asc" ? "Lowest first" : "Highest first"}
                <span aria-hidden="true">{compSort.dir === "asc" ? "▲" : "▼"}</span>
              </button>
            )}
            {/* One set of records, two ways of reading it. The switch is a switch and not a link:
                nothing is fetched, nothing is re-sorted, and nothing about the selection changes. */}
            <div className="vb-comp-views" role="group" aria-label="How to show the comparable sales">
              <button type="button" className="vb-comp-view" aria-pressed={compView === "cards"}
                      onClick={() => setCompView("cards")}>Cards</button>
              <button type="button" className="vb-comp-view" aria-pressed={compView === "table"}
                      onClick={() => setCompView("table")}>Table</button>
            </div>
          </div>
        </div>

        {!compSort && (!bestOffered || arrival) && <p className="vb-comp-sort-hint" id="vb-comp-sort-hint">{SEARCH_ORDER_HINT}</p>}
        {/* BEST MATCH CHOSEN BUT NOT USABLE: said in place, never a silent fallback (no dead control). */}
        {!compSort && bestOffered && !arrival && !bestUsable && (
          <p className="vb-comp-sort-hint" id="vb-comp-sort-hint" data-testid="best-match-unavailable">
            {bestOrderNote ?? "Ranking these sales…"} Until then they show nearest first, then county records.
          </p>
        )}
        {!compSort && bestOffered && !arrival && whyOrderOpen && (
          <p className="vb-comp-sort-hint" id="vb-comp-why-order" role="note" data-testid="best-match-why-text">
            {BEST_MATCH_WHY}
          </p>
        )}

        {/* THE ONE SENTENCE THAT IS NOT AN INSTRUCTION. It says what the set contains and what the
            app will not do to it, so it is never behind a disclosure and never at 12px grey with
            the how-to text. Source-neutral since the cascade (2026-08-11): every sentence on this
            screen used to assume the sales came out of an uploaded file, and since the address-only
            path landed they very often did not. */}
        <p className="vb-comp-claim" style={{ maxWidth: measure.copy }}>
          Every sale we found is listed here, including any that left a field blank. Those rows are
          marked, and their checkbox stays off until you fill the blank in — nothing is ever assumed
          or carried over from another property.
        </p>

        {/* WHICH SALE LEADS, AND THE ONE THING IT DOES NOT DO (2026-09-08, owner).

            SHOWN ONLY ONCE THE AGENT HAS SET A LADDER. A screen that explains a control nobody has
            touched yet is a screen that teaches people to skim, and the control itself already
            says what it is ("Order", "Auto", "1st"). What cannot be read off the control is the
            consequence — and the consequence is a NON-event: the price does not move. That
            sentence is worth its space exactly when somebody has just moved a sale to the top and
            is looking at the figure to see what happened.

            `WEIGHT_NOTE` is `compLadder`'s, which is a duplicate of `comp_primacy.WEIGHT_NOTE`
            pinned to it by a test — not a second wording of one caveat. */}
        {ladderIndices(comps).length > 0 && (
          <p className="vb-comp-claim" style={{ maxWidth: measure.copy }}>
            {/* "A leads the report, then B, then C." — one clause per rung rather than the
                place-word repeated on each ("A leads as the primary comparable AND B leads as the
                secondary comparable"), which is how it read first and is not a sentence anybody
                says. The place words are on the control; here the ORDER is the point. */}
            {ladderIndices(comps).map((at, n) => (
              <span key={comps[at]?.name ?? n}>
                {n === 0 ? "" : ", then "}
                <strong>{comps[at]?.name ?? "this sale"}</strong>
                {n === 0 ? " leads the report" : ""}
              </span>
            ))}
            {". "}
            {WEIGHT_NOTE}{" "}
            <button
              type="button"
              className="vb-comp-more"
              onClick={() => onCompsChange(clearLadder(comps))}
            >
              Put them back in the order listed
            </button>
          </p>
        )}

        <details className="vb-comp-guide">
          <summary className="vb-comp-guide-label">How to read these sales</summary>
          {/* True of the table, and only said there. On the cards the same fact is said where it is
              actionable — each card carries a "Correct a figure" control — and repeating it up here
              would be a paragraph about a cell the reader cannot see. */}
          {compView === "table" && (
            <p style={{ maxWidth: measure.copy }}>
              Sold price, GLA, year, baths, lot and garage are editable — a provider&apos;s number is a starting point,
              not the final word. Correcting one here recomputes the estimate from the corrected figure; the as-pulled
              value stays on record (hover the &quot;edited&quot; mark).
            </p>
          )}
          {/* Only true of the table, so it is only said there. A sentence about dragging a column
              divider on a screen with no columns is the kind of instruction that teaches a person to
              stop reading the instructions. */}
          {compView === "table" && (
            <p style={{ maxWidth: measure.copy }}>
              Click a column heading to sort the sales by it, and again to reverse the order — sorting
              only changes what order they are shown in, never which ones are ticked. Drag the divider
              on the right of any heading to widen or narrow that column. Both last as long as this
              screen is open.
            </p>
          )}
          {compView === "cards" && (
            <p style={{ maxWidth: measure.copy }}>
              Every figure here opens its own source — including price per square foot, which no
              record states and this app works out from the two figures beside it. Switch to the table
              to correct a number, or to see all eighteen fields at once.
            </p>
          )}
        </details>
        {compView === "cards" && (
          <>
          <CompCards
            comps={comps}
            order={compOrder}
            subjectGrid={grid}
            rungLabels={rungLabels}
            onToggle={(i, next) => {
              const list = comps.slice();
              list[i] = { ...list[i], _selected: next, confirmed: next };
              onCompsChange(list);
            }}
            onEdit={editCompAt}
            onSwitchToTable={() => setCompView("table")}
            onLead={(i, position) => onCompsChange(setLeadFromRanking(comps, i, position))}
            onStep={(i, direction) => onCompsChange(stepLead(comps, i, direction))}
            onMove={(i, place) => onCompsChange(moveLead(comps, i, place))}
            activeIndex={compFocus?.activeIndex ?? null}
            onActiveChange={compFocus?.onActiveChange}
            showPinNumbers={!!compFocus}
          />
          </>
        )}

        {/* UI-060: contain the wheel so a gesture over the table falls through to the page scroller
            (a nested overflow container was trapping the wheel and stranding the Generate button). */}
        {compView === "table" && (
        <>
        {/* THE COLUMNS PAST THE EDGE HAVE TO ANNOUNCE THEMSELVES  (2026-09-01, W2-UIFIX).
            Measured from `w1-harness/step3-comps@1920.png`: the eighteen declared widths total
            2062px and a 1920 viewport gives the page 1843, so `Sale type` was sliced down its
            middle and `Source` was not on the screen at all. The container has scrolled since
            2026-08-29 — but Chromium paints an OVERLAY scrollbar, which is invisible until
            something is already scrolling, so the only thing an agent could see was a table that
            stopped mid-column. `.vb-comp-scroll` below forces a classic bar; this sentence is the
            half of the signal that does not depend on seeing it, because a scrollbar at the bottom
            of a 400px-tall region is not where the eye is when it hits the clipped edge. */}
        {/* NOTHING AT ALL WHEN EVERYTHING FITS. `max` is 0 on a monitor wide enough to show all
            eighteen columns, and then this whole row is absent — an affordance for a problem the
            reader does not have is just another thing to read. */}
        {compScroll.max > 0 && (
          <div className="vb-comp-scrollbar">
            <p className="vb-comp-scrollhint">
              {COMP_COLUMNS.length} columns. {Math.round(compScroll.max)}px of them are past the
              right edge at this window size — step through with these, click the table and use the
              arrow keys, or drag its scrollbar. The page itself never moves.
            </p>
            <span className="vb-comp-scrollnav">
              {/* THE LABEL IS THE STATE, NOT THE ARROW. A disabled arrow glyph is a colour
                  difference; "at the first column" is a sentence. Both buttons carry a written
                  name for assistive technology and a `title` for the pointer, and `disabled`
                  carries it to both. */}
              <button
                type="button"
                className="vb-comp-scrollbtn"
                onClick={() => scrollComps(-1)}
                disabled={compScroll.left <= 0}
                title={compScroll.left <= 0 ? "Already at the first column" : "Show earlier columns"}
              >
                <span aria-hidden="true">←</span> Earlier columns
              </button>
              <button
                type="button"
                className="vb-comp-scrollbtn"
                onClick={() => scrollComps(1)}
                disabled={compScroll.left >= compScroll.max - 1}
                title={compScroll.left >= compScroll.max - 1 ? "Already at the last column" : "Show later columns"}
              >
                Later columns <span aria-hidden="true">→</span>
              </button>
            </span>
          </div>
        )}
        <div
          /* The fade is the second signal and never the only one — the sentence above says the
             same thing in words. `data-more` is what turns it off at the end of the run, so it
             never claims there is something past an edge that has nothing past it. */
          className="vb-comp-scrollframe"
          data-more={compScroll.left < compScroll.max - 1 ? "right" : undefined}
          data-back={compScroll.left > 0 ? "left" : undefined}
        >
        <div
          ref={compScrollRef}
          /* READ THE NUMBER NOW, SET STATE WITH THE NUMBER. `setCompScroll(p => …
             e.currentTarget.scrollLeft)` looks equivalent and is not: the updater runs during the
             next render, by which time React has cleared `currentTarget` on the synthetic event,
             and the whole screen unmounts with
             `TypeError: Cannot read properties of null (reading 'scrollLeft')` — caught here by
             clicking the new button in a live browser, which is the only place it shows. */
          onScroll={(e) => {
            const left = e.currentTarget.scrollLeft;
            setCompScroll((prev) => (prev.left === left ? prev : { ...prev, left }));
          }}
          className="vb-comp-scroll"
          /* A SCROLLABLE REGION HAS TO BE REACHABLE FROM THE KEYBOARD. Without `tabIndex` a
             pointerless user cannot reach the columns at all: nothing inside the overflow gets
             focus until the table is already scrolled to it, and there is nothing to scroll it
             with. `role="region"` plus a name is what makes the stop meaningful rather than a
             mystery tab stop (WCAG 2.1.1, and the pattern the ARIA APG gives for exactly this). */
          role="region"
          aria-label="Comparable sales, all columns"
          tabIndex={0}
          style={{ overscrollBehaviorX: "contain" }}
        >
          <table style={{
            width: "100%", borderCollapse: "collapse", fontSize: 13,
            /* THE DECLARED WIDTHS ARE THE LAYOUT, FROM THE FIRST PAINT (2026-08-29, owner, on a
               live Denver run: "that does not look good and needs to be corrected. the positive is
               I am able to adjust the fields and thats good so its easier to read but it should be
               easier to read out of the gate.")

               WHAT WAS WRONG, and the owner's second sentence is the diagnosis. This said
               `tableLayout: colWidths.length ? "fixed" : "auto"` — automatic until the agent drags
               a divider. The intent was reasonable (let the browser size things until somebody
               expresses a preference) and the effect was not: `minWidth` was 1180 while the
               eighteen declared widths total 2062, so on every first paint the browser was handed
               a table 882px narrower than its content and told to make it fit. Automatic layout
               does that by shrinking whatever can shrink, and what can shrink is text: the columns
               holding inputs and selects hold their intrinsic size, so the entire deficit landed on
               `Comp` and `Neighborhood`. `Comp` collapsed to its longest word. "1700 Bassett Street
               Unit 1303" rendered one word per line down a ~40px strip, the blocked-row note under
               it did the same, the row grew to roughly 550px tall, and the heading truncated to
               "Com" — on the screen where an agent chooses which sales a valuation is built from.

               The moment the agent dragged ANY divider, `colWidths` became non-empty, the layout
               flipped to fixed, every column got the width it was always supposed to have, and the
               screen became readable. That is exactly what he described, and it is why the fix is
               not to tune the automatic path but to delete it: the declared widths in
               `COMP_COLUMNS` were never being used at all.

               So: fixed always, seeded from the declared widths, and `minWidth` is now their true
               sum rather than a smaller number that guaranteed the squeeze. The table overflows its
               own `overflow-x` container and scrolls — which is the behaviour the owner asked for
               in the first place ("I like having the horizontal scroll bar when needed") and is
               strictly better than crushing a column to one word. */
            tableLayout: "fixed", minWidth: COMP_COLUMNS_TOTAL_WIDTH,
          }}>
            <colgroup>
              {COMP_COLUMNS.map((col) => (
                <col
                  key={col.key}
                  data-col={col.key}
                  /* A dragged width wins; otherwise the column's declared width. There is no third
                     case any more — a `<col>` with no width under a fixed layout is what let the
                     browser improvise. */
                  style={{ width: colWidths[col.key] ?? col.width }}
                />
              ))}
            </colgroup>
            <thead>
              {/* COLUMN GROUP BANDS (2026-08-30, from the walkthrough prototype).
                  Eighteen column headings in one flat row is eighteen things to read before the
                  first sale. The prototype bands them into four ideas — which sale this is, what it
                  did on the market, what the record says about the house, and where the figure came
                  from — so the eye picks a REGION first and a column second.

                  Derived from `COMP_COLUMNS`, not written out: `GROUP_SPANS` names the group each
                  key belongs to and the spans are counted from the live column list, so adding a
                  nineteenth column cannot leave the band row a cell short of the header row beneath
                  it — the classic way a banded table tears. A key in no group falls into the
                  trailing unnamed band rather than being dropped. */}
              <tr aria-hidden="true" className="vb-comp-bands">
                {compGroupBands.map((band, i) => (
                  <th
                    key={band.label + i}
                    colSpan={band.span}
                    scope="colgroup"
                    className={band.label ? "vb-comp-band" : "vb-comp-band vb-comp-band-blank"}
                  >
                    {band.label}
                  </th>
                ))}
              </tr>
              {/* Named, because `thead` now holds TWO rows and anything indexing a column has to
                  say which one it means — including the tests, which map a heading's position onto
                  a body cell's position and would silently read the wrong cell otherwise. */}
              <tr className="vb-comp-heads" style={{ textAlign: "left", color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>
                {COMP_COLUMNS.map((col) => {
                  const active = !!col.sort && compSort?.key === col.sort;
                  const dir = active ? compSort!.dir : null;
                  return (
                    <th
                      key={col.key}
                      scope="col"
                      // The sort state is carried by `aria-sort` rather than by the arrow glyph
                      // alone — the glyph is decoration, and a column that is sorted must say so
                      // to a reader who cannot see it.
                      aria-sort={col.sort ? (dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none") : undefined}
                      style={{ padding: "6px 8px", position: "relative", verticalAlign: "bottom",
                               overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {col.sort ? (
                        <button
                          type="button"
                          onClick={() => toggleCompSort(col.sort!)}
                          title={`Sort the sales by ${col.label.toLowerCase()}${active ? " — click again to reverse" : ""}`}
                          style={{ border: "none", background: "transparent", padding: 0, margin: 0,
                                   font: "inherit", color: active ? colors.accent : colors.textSecondary,
                                   fontWeight: active ? 600 : 400, cursor: "pointer", textAlign: "left" }}
                        >
                          {col.label}
                          <span aria-hidden="true" className="vb-sort-glyph" data-active={active || undefined}
                                style={{ marginLeft: 4 }}>
                            {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅"}
                          </span>
                        </button>
                      ) : (
                        col.label || " "
                      )}
                      {!col.fixed && (
                        /* THE DIVIDER IS A REAL CONTROL, not a cursor change on a border. It is
                           focusable and takes the arrow keys, because a column width is exactly
                           the kind of adjustment that is impossible to make with a trackpad on a
                           laptop in a listing appointment. `role="separator"` with an orientation
                           is what a resizer between two regions is. */
                        <span
                          role="separator"
                          aria-orientation="vertical"
                          aria-label={`Resize the ${col.label} column`}
                          tabIndex={0}
                          data-resize={col.key}
                          onPointerDown={(e) => beginColumnDrag(e, col)}
                          onKeyDown={(e) => {
                            if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
                            e.preventDefault();
                            const step = e.key === "ArrowRight" ? 16 : -16;
                            resizeColumn(col, e.currentTarget, (base) => base + step);
                          }}
                          style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 9,
                                   /* INVISIBLE AT REST (2026-08-30). This painted a 2px rule on the right of every
                                      non-fixed heading, so the header row carried seventeen vertical
                                      dividers while the body carried none — the Access-97 look, on the
                                      app's most important screen. It resolves on hover, exactly like
                                      `.vb-sort-glyph` two lines away already does. */
                                   cursor: "col-resize", borderRight: "2px solid transparent",
                                   touchAction: "none" }}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {compOrder.map((i) => {
                const c = comps[i];
                /* THE PROVIDER TEST, ASKED OF THE PAYLOAD RATHER THAN OF A LITERAL (fixed
                   2026-08-11). This was `c._source === "cotality"` — a value the server has never
                   written — so it was false on every real row and the two branches that depend on
                   it have never fired. `isProviderRow` compares against `rung_labels.cotality` as
                   the cascade reports it; see the `rungLabels` prop. */
                const isProviderCandidate = isProviderRow(c, rungLabels);
                /** AWAITING A SOLD PRICE (2026-07-31). A real closed sale the file described
                 * completely except for its sale price — many MLS report formats print listing
                 * detail and omit the sale financials even when Status is Closed. Such a row used
                 * to be DELETED for that, which is how a file holding three sold comps produced
                 * "Only 0 closed sale(s) were read". Read from the live value, so typing the price
                 * clears this immediately. */
                const needsPrice = c.price == null || Number(c.price) <= 0;

                /** EVERY REQUIRED FIELD THIS ROW IS STILL SHORT (2026-08-04).
                 *
                 * `_coerce` no longer deletes a comp missing GLA, lot size, year or garage — the
                 * measured case is an NTREIS export that prints `Lot Dimen:  Acres:   Cov Prk: 2`,
                 * a blank where the acreage should be, on five of ten records. There is no lot
                 * size anywhere on that page, so no reader can recover one; the only person who
                 * can is the agent, and they could not, because the row had been removed from the
                 * screen that invited them to fix it.
                 *
                 * Derived from the LIVE row (never from the server's `needs_fields` flag alone),
                 * so typing the value clears the warning and un-disables the box on the very next
                 * render — the same rule `needsPrice` already follows. */
                const missingFields = compMissingFields(c as unknown as Record<string, unknown>);
                const missingOther = missingFields.filter((k) => k !== "price");
                const blocked = missingFields.length > 0;
                const label = (k: string) =>
                  COMP_REQUIRED_FIELDS.find((f) => f.key === k)?.label ?? k;
                const blockedTitle = blocked
                  ? `Enter this sale's ${missingFields.map(label).join(", ")} to include it — your file didn't state ${missingFields.length === 1 ? "it" : "them"}`
                  : undefined;
                /** The caution ring on an input the row is actually waiting on. Colour alone would
                 * be the only signal, so the title carries the same fact for a screen reader. */
                const fieldNeeded = (k: string) => missingFields.includes(k as never);

                /** Apply a human edit to one or more comp fields, snapshotting the as-pulled value
                 * into `_raw` on FIRST divergence per field (never re-snapshotting on later edits —
                 * same rule as the subject grid's `editField`). */
                const editComp = (patch: Partial<CandidateComp>) => editCompAt(i, patch);

                return (
                  <Fragment key={`${c.name ?? "comp"}-${i}`}>
                  <tr
                    className={"vb-comp-row" + (c._selected ? " vb-comp-row-on" : "")}
                    style={{ borderTop: `1px solid ${colors.border}` }}
                    data-comp-index={i}
                    data-active={compFocus?.activeIndex === i || undefined}
                    onMouseEnter={compFocus ? () => compFocus.onActiveChange(i) : undefined}
                    onMouseLeave={compFocus ? () => compFocus.onActiveChange(null) : undefined}
                    onFocus={compFocus ? () => compFocus.onActiveChange(i) : undefined}
                    onBlur={compFocus ? () => compFocus.onActiveChange(null) : undefined}
                  >
                    <td className="vb-comp-check-cell">
                      <input
                        className="vb-comp-check"
                        type="checkbox"
                        checked={!!c._selected}
                        disabled={blocked}
                        title={blockedTitle}
                        aria-label={`Include ${c.name ?? "this comp"}`}
                        onChange={(e) => {
                          const next = comps.slice();
                          next[i] = { ...c, _selected: e.target.checked, confirmed: e.target.checked };
                          onCompsChange(next);
                        }}
                      />
                    </td>
                    {/* WHERE THIS SALE STANDS (2026-09-08, owner).

                        A PLAIN SELECT, not a drag handle. Dragging three rows into order in a
                        nineteen-column scrolling table is a fine demo and a bad control: it needs
                        a mouse, it needs aim, and it cannot be reached from a keyboard at all. A
                        select says what the choices are, says which one is current, and works
                        everywhere.

                        IT ONLY OFFERS PLACES THAT EXIST YET — one more than are already taken.
                        With nothing set the list is "Auto" and "1st", so the control teaches the
                        ladder rather than letting the agent build a third-with-no-second and then
                        be refused for it by `comp_primacy.validate`.

                        DISABLED ON A ROW THAT CANNOT BE PRICED, on the same condition as the tick
                        beside it: a sale missing a required figure is not in the brief, and a
                        sale that is not in the brief cannot lead it. */}
                    <td className="vb-comp-lead-cell" style={{ padding: "6px 8px" }}>
                      <select
                        className="vb-comp-lead"
                        value={leadOf(c) ?? ""}
                        disabled={blocked || !c._selected}
                        title={blocked
                          ? blockedTitle
                          : !c._selected
                            ? "Include this sale first, then it can lead the report"
                            : `Where ${c.name ?? "this sale"} sits in the report`}
                        aria-label={`Order of ${c.name ?? "this sale"} in the report`}
                        onChange={(e) => {
                          const v = e.target.value;
                          setLeadAt(i, v === "" ? null : (Number(v) as LeadPosition));
                        }}
                      >
                        {/* "Auto" is a real answer, not an absence: it is the place the analysis
                            ranked this sale, and saying so is better than an empty option. */}
                        <option value="">Auto</option>
                        {availablePositions(comps, i).map((pos) => (
                          <option key={pos} value={pos}>
                            {LEAD_SHORT[pos]}
                          </option>
                        ))}
                      </select>
                    </td>
                    {/* WHEN IT SOLD, FIRST ON THE ROW (2026-08-10) — see `COMP_COLUMNS`. Read-only
                        for the same reason the neighbourhood is: the closing date is a fact of
                        record, not a judgment, and this client has never had a route to write one
                        back. A sale the file printed no date for shows a dash and is sorted to the
                        bottom of a date sort rather than being treated as the oldest. */}
                    <td style={{ padding: 8, whiteSpace: "nowrap" }}>
                      {saleDateLabel(c.sale_date)}
                    </td>
                    <td style={{ padding: 8 }}>
                      {/* THE STREET LINE STAYS ON ONE LINE (2026-08-29). The table lays out on the
                          browser's own algorithm until an agent drags a divider, and left to itself
                          it was breaking "4118 Ridgemont Drive" across three lines while giving the
                          pool column room for the word "No" — so the row's identity, the one thing
                          you scan a comparable set by, was the least legible cell on the row. */}
                      <span style={{ whiteSpace: "nowrap", fontWeight: 500 }}>{c.name ?? "—"}</span>
                      {/* THE RECORD THIS ROW CAME FROM (2026-08-10). The extractor has read the
                          MLS number since the field was added and nothing showed it, so an agent
                          checking a figure against their own MLS had only a street address to go
                          on — and two sales on one street is not a rare export. */}
                      {/* CITY AND STATE, UNDER THE STREET LINE (2026-08-11). Rendered only when
                          the source states them — a dash here would claim we looked and found no
                          city, which is not the same as a file that never carried one. */}
                      {(c.city || c.state) && (
                        <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                          {[c.city, c.state].filter(Boolean).join(", ")}
                        </div>
                      )}
                      {c.mls_id && (
                        <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2,
                                      fontFamily: "monospace" }}>
                          MLS #{c.mls_id}
                        </div>
                      )}
                      {/* THE ROW SAYS WHAT IT IS WAITING ON (2026-08-04). Only for gaps OTHER than
                          the price, which already has its own message in the next cell — printing
                          both would say the same thing twice on the same row. */}
                      {/* AND IT ONLY BLAMES A FILE WHEN THERE WAS ONE (2026-08-11). "Your file
                          doesn't state it" is exactly right for an uploaded row and simply untrue
                          on a public-record candidate — on the cascade's address-only path there is
                          no file at all, and every row on the screen was saying otherwise. The
                          reason a provider row is blank is structural, not a shortcoming of
                          something the agent did: the comparables feed carries no parking field and
                          no bath split (spec §3). Same fact either way — only you can supply it. */}
                    </td>
                    {/* READ-ONLY, BY DESIGN. No input, no onChange — a typed neighbourhood or a
                        typed distance would be a claim about where a house is, and that is the one
                        class of fact an agent must not be able to assert into a valuation. */}
                    <td style={{ padding: 8 }}>{c.subdivision || "—"}</td>
                    <td style={{ padding: 8, fontFamily: "monospace", whiteSpace: "nowrap" }}>
                      {typeof c.distance === "number" && Number.isFinite(c.distance)
                        ? `${c.distance.toFixed(2)} mi`
                        : "—"}
                    </td>
                    <td style={{ padding: 8, fontFamily: "monospace", whiteSpace: "nowrap" }}>
                      {daysOnMarketLabel(c, c.dom_as_of)}
                      {cumulativeDaysOnMarketLabel(c) && (
                        <div style={{ fontSize: 11, color: colors.textSecondary, fontFamily: "system-ui, sans-serif" }}>
                          {cumulativeDaysOnMarketLabel(c)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 8, fontFamily: "monospace" }}>
                      <CompNumInput
                        value={c.price}
                        edited={c._raw != null && "price" in c._raw}
                        original={c._raw?.price ?? undefined}
                        format="money"
                        onChange={(v) => editComp({ price: v })}
                      />
                      {/* The box beside this row is disabled until a price exists; say why here,
                          next to the field that fixes it, rather than only in a hover title. */}
                      {needsPrice && (
                        <div style={{ fontSize: 11, color: colors.caution, marginTop: 3,
                                      fontFamily: "system-ui, sans-serif", whiteSpace: "normal" }}>
                          Sold price needed — your file shows only the asking price
                          {c.list_price != null ? ` ($${Number(c.list_price).toLocaleString()})` : ""}
                        </div>
                      )}
                      {/* WHAT IT WAS ASKING (2026-08-10). The asking price was already read and was
                          shown ONLY on a row missing its sold price. Beside a completed sale it is
                          the other half of the "listed at X, sold for Y in N days" sentence the
                          report tells — the agent is being asked to judge which sales describe
                          this market, and how far each one moved off its list price is exactly
                          that judgment. */}
                      {!needsPrice && c.list_price != null && Number.isFinite(Number(c.list_price)) && (
                        <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 3,
                                      fontFamily: "system-ui, sans-serif" }}>
                          Asked ${Number(c.list_price).toLocaleString()}
                        </div>
                      )}
                      <CellSource comp={c} field="price" />
                    </td>
                    {/* UI-063 / owner ruling 2026-07-28. A blank concession never halts a build:
                        the engine treats an unstated concession as a usable $0 (`assumed_zero`). But
                        an app-assumed zero is NOT the same fact as a $0 the source actually stated,
                        so it is labelled as an assumption rather than wearing a source-verified
                        badge. That distinction is the whole point — the realtor never has to type 0,
                        and the report never claims the file said something it did not. */}
                    {/* ⚠️ AN AGENT'S TYPED CONCESSION IS A HUMAN CONFIRMATION, AND THE ENGINE HAS
                        TO BE TOLD IN THE ONE VOCABULARY IT READS (2026-08-10).

                        This cell used to write `concession_status: "human_confirmed"` and nothing
                        else. `concession_status` is an OUTPUT: `engine.amb.comp_concession_status`
                        (amb.py:397-402) and the adjustment loop (amb.py:1132-1133) recompute it on
                        every run from `seller_concession_source == "human"` or
                        `concession_human_confirmed`, and never look at the key this cell was
                        setting. `server/amb_extract.py:3521` stamps `"source"` on every extracted
                        comp, so the confirmation never happened as far as the valuation was
                        concerned.

                        WHAT THAT COST, on the owner's own Springtree file: 4404 Coldbrook Lane
                        carries $26,000 of concessions on a $358,000 sale — 7.26%, above the 6%
                        review threshold and under the 10% bound, so it classifies
                        `review_required`, and `engine.amb.screen_concessions` (amb.py:419-428)
                        does not downweight such a comparable, it REMOVES it: out of the adjustment
                        grid, out of the reconciliation, out of the narrative. The agent typed the
                        confirmation the engine was asking for, this cell painted a green "You
                        confirmed this" underneath it — reading back the same invented key — and
                        the sale left the valuation anyway. If the exclusions took the usable set
                        below three, the build then halted asking the agent to do the thing they
                        had just done. Below 6% nothing broke visibly and the report simply
                        credited the file with a number the agent had supplied.

                        The legacy client has had this right since Part C
                        (`server/web/index.html:3077`: `all[i].seller_concession_source='human'`),
                        pinned by `tests/test_index_js_concession.py:307-311`. This is the same
                        contract, written from the same evidence.

                        `concession_status` is STILL set, because `ConcessionNote` below reads it to
                        label the cell — but it is now derived the way the engine derives it
                        (`concessionStatusAfterHumanEdit`), so a cleared cell says "assumed $0" and
                        a figure past the absolute 10% bound says it is blocked, instead of every
                        keystroke claiming a confirmation the engine would refuse. */}
                    <td style={{ padding: 8, fontFamily: "monospace", whiteSpace: "nowrap" }}>
                      <CompNumInput
                        value={c.seller_concession ?? null}
                        edited={c._raw != null && "seller_concession" in c._raw}
                        format="money"
                        width={90}
                        onChange={(v) => editComp({
                          seller_concession: v,
                          seller_concession_source: "human",
                          concession_status: concessionStatusAfterHumanEdit(c.price, v),
                        })}
                      />
                      <ConcessionNote status={c.concession_status} value={c.seller_concession ?? null} />
                      {/* WHICH PRINTED FIELD THE DOLLARS CAME FROM (2026-08-10). The extractor is
                          asked for the label precisely so the agent can see it, and it was carried
                          to the client and dropped. It matters on an NTREIS sheet, where "Seller
                          Concessions" often says only "Yes" while the money sits in the closing
                          block's "Slr Paid" — two fields, one of which is the one we read.
                          Withdrawn once the agent overrides the figure: the label describes where
                          the FILE's number came from, and it is no longer the number on screen. */}
                      {c.seller_concession_label && !(c._raw != null && "seller_concession" in c._raw) && (
                        <span style={{ display: "block", marginTop: 2, fontSize: 11, color: colors.textSecondary,
                                       fontFamily: "system-ui, sans-serif", whiteSpace: "normal", maxWidth: 150 }}>
                          Read from &ldquo;{c.seller_concession_label}&rdquo;
                        </span>
                      )}
                    </td>
                    <td style={{ padding: 8, fontFamily: "monospace" }}>
                      <CompNumInput
                        value={c.gla}
                        edited={c._raw != null && "gla" in c._raw}
                        original={c._raw?.gla ?? undefined}
                        format="grouped"
                        needed={fieldNeeded("gla")}
                        onChange={(v) => editComp({ gla: v })}
                      />
                      {/* THE SHEET'S OWN $/SF (2026-08-10). Transcribed by the extractor, never
                          recomputed here — a "stated" figure the app derived would not be a
                          statement by the file. It sits under GLA because it is the arithmetic tie
                          between GLA and the sold price, and `engine.amb.validate_comp` HALTS the
                          build when the two disagree with it. An agent correcting either number
                          can now see the third one it has to stay consistent with, before the
                          round trip rather than after it. */}
                      {typeof c.stated_ppsf === "number" && Number.isFinite(c.stated_ppsf) && (
                        <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 3,
                                      fontFamily: "system-ui, sans-serif" }}>
                          File states ${c.stated_ppsf.toFixed(2)}/SF
                        </div>
                      )}
                      <CellSource comp={c} field="gla" />
                    </td>
                    {/* BEDROOMS — display only. See `COMP_COLUMNS`: the engine has no bedroom line
                        and must not grow one, because bedroom count is already inside living
                        area. This is here so an agent can SEE a 3-bed sale beside a 4-bed
                        subject and decide whether it belongs in the set. */}
                    <td style={{ padding: 8, fontFamily: "monospace" }}>
                      {typeof c.beds === "number" && Number.isFinite(c.beds) ? c.beds : "—"}
                    </td>
                    <td style={{ padding: 8, fontFamily: "monospace" }}>
                      <CompNumInput
                        value={c.year}
                        edited={c._raw != null && "year" in c._raw}
                        original={c._raw?.year ?? undefined}
                        width={60}
                        needed={fieldNeeded("year")}
                        onChange={(v) => editComp({ year: v })}
                      />
                      <CellSource comp={c} field="year" />
                    </td>
                    {/* THE BATH SPLIT IS THE CASCADE'S HEADLINE GAP (spec §3): the comparables feed
                        carries one combined `baths` float and no full/half split at all, so on a
                        public-record row both of these are blank by construction and both are the
                        agent's to fill. The tag under them says so once they do. */}
                    <td style={{ padding: 8, fontFamily: "monospace" }}>
                      <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <CompNumInput
                          value={c.full_baths}
                          edited={c._raw != null && "full_baths" in c._raw}
                          original={c._raw?.full_baths ?? undefined}
                          width={44}
                          onChange={(v) => editComp({ full_baths: v })}
                        />
                        /
                        <CompNumInput
                          value={c.half_baths}
                          edited={c._raw != null && "half_baths" in c._raw}
                          original={c._raw?.half_baths ?? undefined}
                          width={44}
                          onChange={(v) => editComp({ half_baths: v })}
                        />
                      </span>
                      <CellSource comp={c} field="full_baths" />
                    </td>
                    <td style={{ padding: 8, fontFamily: "monospace" }}>
                      <CompNumInput
                        value={c.lot_sf}
                        edited={c._raw != null && "lot_sf" in c._raw}
                        original={c._raw?.lot_sf ?? undefined}
                        format="grouped"
                        width={76}
                        needed={fieldNeeded("lot_sf")}
                        onChange={(v) => editComp({ lot_sf: v })}
                      />
                      {/* A LOT SIZE THE FILE DID NOT PRINT AS SQUARE FEET (2026-08-10). Many MLS
                          sheets give acres and leave exact square feet blank, and the server
                          converts (`_fill_lot_from_acres`) rather than losing the comp. The
                          arithmetic is exact and the conversion is already disclosed for the file
                          as a whole in the reading notes; this is the same disclosure on the row it
                          is actually about, so an agent checking a lot adjustment can see at a
                          glance which figures were printed and which were derived. Withdrawn once
                          the agent types their own lot size — it then describes a number that is
                          no longer on screen. */}
                      {c.lot_sf_source && !(c._raw != null && "lot_sf" in c._raw) && (
                        <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 3,
                                      fontFamily: "system-ui, sans-serif", whiteSpace: "normal" }}>
                          Converted: {c.lot_sf_source}
                        </div>
                      )}
                      <CellSource comp={c} field="lot_sf" />
                    </td>
                    {/* GARAGE BAYS. Required by the engine, absent from this grid until 2026-08-04.
                        `0` is a real answer — a house with no garage — so the readiness rule tests
                        for null rather than falsiness and a typed 0 satisfies it. */}
                    <td style={{ padding: 8, fontFamily: "monospace" }}>
                      <CompNumInput
                        value={c.garage}
                        edited={c._raw != null && "garage" in c._raw}
                        original={c._raw?.garage ?? undefined}
                        width={52}
                        needed={fieldNeeded("garage")}
                        onChange={(v) => editComp({ garage: v })}
                      />
                      <CellSource comp={c} field="garage" />
                    </td>
                    {/* POOL — READ-ONLY, AND THREE-STATE (2026-08-10). See `COMP_COLUMNS` for why
                        it is on the screen at all (the engine prices a pool at $25,000 or 5% of
                        the sale price, off this flag, and the flag was invisible) and why it is
                        not yet editable (an unticked checkbox would assert "no pool" on a file
                        that never said). A dash means the record did not state one; it is not a
                        "no", and the engine's own treatment of an unstated pool as no-pool is a
                        separate fact the agent can at least now see the shape of. */}
                    <td style={{ padding: 8 }}>
                      {c.pool === true ? "Yes" : c.pool === false ? "No" : "—"}
                    </td>
                    <td style={{ padding: 8 }}>
                      <select
                        /* NEVER THE FIRST OPTION BY ACCIDENT (2026-08-13). Was `value={c.condition}`.
                           A Cotality comp carries no condition, `<select>` fell through to option
                           zero, and the ladder's best rung is option zero — so every provider comp
                           read "Renovated" on screen while the engine computed "average". See
                           `normalizeCondition` in constants.ts for the full account. */
                        value={normalizeCondition(c.condition)}
                        aria-label={`Condition of ${c.name ?? "this comp"}`}
                        onChange={(e) => {
                          const next = comps.slice();
                          next[i] = { ...c, condition: e.target.value };
                          onCompsChange(next);
                        }}
                      >
                        {CONDITIONS.map((cond) => (
                          <option key={cond} value={cond}>
                            {CONDITION_LABELS[cond]}
                          </option>
                        ))}
                      </select>
                      {/* WHY THAT LABEL (2026-08-10). `source_note` is the extractor's own
                          justification for the condition it chose — "one short phrase from the
                          remarks that justifies the condition label", transcribed verbatim, never
                          written. Condition is the largest single lever on this screen (5% of each
                          comp's own sale price per step), and until now the agent was asked to
                          accept or overrule a machine's judgment with none of the machine's
                          evidence in front of them. The full remarks the phrase came from are on
                          hover rather than in the cell: they run to 600 characters and belong in
                          the narrative, not in a table row. */}
                      {c.source_note && (
                        <div
                          title={c.remarks ?? undefined}
                          style={{ fontSize: 11, color: colors.textSecondary, marginTop: 3,
                                   lineHeight: 1.4, whiteSpace: "normal", maxWidth: 220 }}
                        >
                          {c.source_note}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 8 }}>
                      <select
                        value={c.sale_type}
                        aria-label={`Sale type of ${c.name ?? "this comp"}`}
                        onChange={(e) => {
                          const next = comps.slice();
                          next[i] = { ...c, sale_type: e.target.value };
                          onCompsChange(next);
                        }}
                      >
                        {SALE_TYPES.map((st) => (
                          <option key={st} value={st}>
                            {SALE_TYPE_LABELS[st] ?? st}
                          </option>
                        ))}
                      </select>
                    </td>
                    {/* THE ROW'S SOURCE, IN THE WORDS THE SOURCE ITSELF USES (fixed 2026-08-11).
                        This printed one of two hardcoded phrases chosen by `_source === "cotality"`
                        — a comparison that never matched, so every row on every real job read "Your
                        uploaded file", including the provider candidates the server refuses to
                        build from unreviewed. `_source` is already the printed label
                        (`source_cascade._row_source` carries the highest rung's tag verbatim), so
                        it is simply shown. A row with none predates the cascade and can only have
                        come from an upload, which is what the fallback says. */}
                    <td style={{ padding: 8, fontSize: 12, color: colors.textSecondary }}>
                      {c._source?.trim() || "Your uploaded file"}
                    </td>
                  </tr>
                  {/* WHAT THIS ROW IS WAITING ON, ON A LINE OF ITS OWN (UI-140, 2026-08-29).
                      This sentence used to live inside the comp-name cell — a 210px column that
                      already held the street line, the city and the MLS number, so on a narrow
                      screen the one thing that explains why a sale cannot be used was the hardest
                      thing on the row to read. The column most likely to go unread must never be
                      the column that discloses the weakness.

                      `.vb-row-note` is `position: sticky; left: 0`, so the sentence stays at the
                      left edge of the viewport while the eighteen columns scroll under it: it is
                      reachable at 390px without scrolling sideways to find it. */}
                  {missingOther.length > 0 && (
                    <tr className="vb-comp-row" data-comp-note="true">
                      <td colSpan={COMP_COLUMNS.length} style={{ padding: "0 8px 10px", borderTop: "none" }}>
                        <div className="vb-row-note"
                             style={{ fontSize: 12, color: colors.caution, lineHeight: 1.45,
                                      fontFamily: typeTokens.fontUi }}>
                          {/* THE SENTENCE IS UNCHANGED, WORD FOR WORD. Only where it renders has
                              moved. It does not repeat the sale's address either: the note sits
                              directly under its own row, and a second copy of the address would be
                              a second thing on the screen claiming to be that sale. */}
                          Needs {missingOther.map(label).join(" and ")} —{" "}
                          {isProviderCandidate ? "the public record doesn't carry" : "your file doesn't state"}{" "}
                          {missingOther.length === 1 ? "it" : "them"}. Enter{" "}
                          {missingOther.length === 1 ? "it" : "them"} above to include this sale.
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>
        </>
        )}
        {/* GROUPED, for the same reason the panel above the table is (2026-08-30). These two are
            about what TICKING a row does, so they belong together and they belong after the rows
            rather than before them. Same panel, same measure; the only difference is the label. */}
        <div className="vb-comp-guide" style={{ maxWidth: measure.copy, marginTop: 14 }}>
          <p className="vb-comp-guide-label">What ticking a sale does</p>
          <p style={{ fontSize: 12, color: colors.textSecondary, margin: "0 0 8px" }}>
            {/* Provider-neutral since 2026-10-20 (item 1F): no screen a regular user reads names a
                provider, and the rule is the same for public-record and MLS candidates. */}
            A comp from a public-records or MLS search can only feed the report once its checkbox is explicitly checked here — the server
            refuses an unreviewed provider candidate outright, even if this step is skipped by another client.
          </p>
          <p style={{ fontSize: 12, color: colors.textSecondary, margin: "0 0 8px" }}>
            Typing a sold price on a sale whose record didn&apos;t carry one puts that row back in the
            selected set, up to the {floor} the report needs. Past that, which sales to price off is
            your call — tick and untick freely.
          </p>
        </div>
      </section>}

      {/* Street View reference photos — Session 8. Display-only context, never a substitute for a
          human-uploaded subject photo and never itself part of the PDF/DOCX deliverable (see
          StreetViewCell's docstring for the license gates this respects). Rendered per comp plus the
          subject, so the agent can sanity-check location/curb context alongside the numbers above.

          ONCE PER SUBJECT (2026-08-05). The image is keyed on the address and nothing else, so a
          second screen carrying the same address rendered a byte-identical photo under the same
          heading — one more thing the agent read as being asked to check twice. */}
      {/* THE HEADING TRAVELS WITH THE PHOTO (2026-08-30). It used to be printed here, outside the
          component that decides whether there IS a photo — and `StreetViewCell` returns null
          whenever the Street View provider is off, which is the shipped default. The result was
          "REFERENCE PHOTO (SUBJECT)" over an empty gap on every subject on every deployment as
          currently configured. Passing the heading in means the two cannot disagree. */}
      {showSubject && addressLine && !hideReferencePhoto && (
        <section style={{ marginTop: 24 }}>
          <StreetViewCell address={addressLine} label="Subject" heading="Reference photo (subject)" />
        </section>
      )}
    </div>
  );
}

/**
 * WHERE ONE FIGURE CAME FROM, WHEN IT IS NOT WHERE THE ROW CAME FROM (2026-08-11, the source
 * cascade). A quiet line under the cell, in the server's own words — "Agent verified sales price",
 * "Cotality (public-record aggregate)", "Uploaded file".
 *
 * QUIET IS THE SPECIFICATION, not a style preference. Owner, on the agent tag: "just say agent
 * verified, dont make a big deal about it." So: no banner, no callout, no colour, no icon, and no
 * paragraph — 11px secondary text, the same weight the "Converted: acres x 43,560" and "Read from
 * 'Slr Paid'" lines already carry two cells away, because it is the same kind of statement.
 *
 * IT RENDERS ONLY WHEN IT SAYS SOMETHING NEW. `cellProvenance` withholds the tag when it merely
 * repeats the row's Source column, which is the ordinary case: a comp read whole from one place has
 * every field tagged with that place, and eighteen copies of it is the big deal nobody asked for.
 * What is left is exactly the interesting case — a sale price the agent supplied on a public-record
 * row, a lot size the public record filled on an uploaded row.
 *
 * THE WORDING IS NEVER THIS FILE'S. Two of the strings that can appear here are owner-set verbatim
 * (`source_cascade.AGENT_TAG` / `AGENT_PRICE_TAG`) and the rest are rung labels; all of them arrive
 * in `_field_source_labels`. Nothing here composes, truncates or embellishes one.
 */
function CellSource({ comp, field }: { comp: CandidateComp; field: string }) {
  const label = cellProvenance(comp, field);
  if (!label) return null;
  return (
    <div
      data-field-source={field}
      style={{ fontSize: 11, color: colors.textSecondary, marginTop: 3,
               fontFamily: "system-ui, sans-serif", whiteSpace: "normal" }}
    >
      {label}
    </div>
  );
}

/** A compact editable numeric cell for the comp table, with the same override-provenance display
 * as the subject grid: an "edited" mark with the as-pulled value on hover once it diverges. */

function FieldInput({
  def,
  state,
  edited,
  digits,
  garageAreaNote,
  onDetail,
  onChange,
}: {
  /** The county's garage area, worded as what it is, shown under the garage dropdown. */
  garageAreaNote?: string | null;
  /** Only the HOA row: the multi-part answer writes through this instead of `onChange`. */
  onDetail?: (d: SubjectFieldDetail) => void;
  def: (typeof SUBJECT_FIELD_SCHEMA)[number];
  state: SubjectGrid[string];
  /** Show a number to this many decimals (display only): a computed acreage, `COMPUTED_DIGITS`. */
  digits?: number;
  /** Session 8: true once this field has diverged from a real as-pulled source value — draws the
   * same caution-colored border the comp table's `CompNumInput` uses, one consistent "edited"
   * visual language across both grids. */
  edited: boolean;
  onChange: (value: string | number | boolean | null) => void;
}) {
  /*
   * THE SAME QUIET FIELD THE COMPARABLE GRID USES (2026-08-29). Twenty-two bordered inputs stacked
   * down a page is the same wall of boxes the comp table was, and the same fix applies: the control
   * draws no box until the pointer or the keyboard is on it, so the VALUE is what the eye lands on.
   * Still a field, still focusable, still labelled; only its paint changed.
   *
   * `data-edited` keeps the one consistent "edited" visual language across both grids — it is a
   * dotted caution underline here and in `CompNumInput`, and the word "· edited" beside it in the
   * source column carries the same fact in text, so colour is never the only signal.
   */
  /*
   * LEFT, NOT RIGHT, AND FOR A REASON. `.vb-cell` right-aligns because in the comparable grid a
   * column is one attribute across six sales and the eye compares down it. Here a column is
   * twenty-two DIFFERENT attributes — a square footage above a year above a bedroom count — and
   * there is nothing to compare, so one clean left edge reads better than three magnitudes
   * pretending to line up.
   */
  // AN EMPTY CELL LOOKS LIKE A FIELD (2026-10-18, owner code prompt Slice B). The quiet field
  // above is right for a figure that is there; for a blank one it hid the fact that the row takes
  // typing at all (prod, 937 Field Trail: a "—" that opened a box only when clicked). `data-empty`
  // draws a visible border, an "Enter" placeholder and a focus ring (app.css), on blank rows only.
  const empty = state.value == null || state.value === "";
  const common = { className: "vb-cell vb-cell-left", "data-edited": edited || undefined,
                   "data-empty": empty || undefined } as const;

  if (def.key === "hoa" && onDetail) {
    return <HoaControl state={state} className="vb-cell vb-cell-left" onDetail={onDetail} />;
  }
  if (def.kind === "boolean") {
    return (
      <select
        {...common}
        aria-label={def.label}
        value={state.value === true ? "true" : state.value === false ? "false" : ""}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "true")}
      >
        <option value="">Not stated</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  if (def.kind === "number") {
    const box = (
      <input
        {...common}
        type="number"
        value={state.value == null ? "" : digits != null ? Number(state.value).toFixed(digits) : Number(state.value)}
        placeholder="Enter"
        aria-label={def.label}
        // A number input captures the wheel: scrolling over it would change the value and eat the
        // gesture. Same guard the comparable grid has carried since UI-064.
        onWheel={(e) => e.currentTarget.blur()}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      />
    );
    if (garageAreaNote) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
          {box}
          <span data-garage-area style={{ fontSize: 11, color: colors.textSecondary }}>{garageAreaNote}</span>
        </div>
      );
    }
    if (!def.unit) return box;
    // The unit sits beside the box, in words, so a bare number is never a guess at its unit.
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
        {box}
        <span data-unit style={{ fontSize: 12, color: colors.textSecondary, whiteSpace: "nowrap" }}>{def.unit}</span>
      </div>
    );
  }
  return (
    <input
      {...common}
      type="text"
      // Words, not figures: the app's own face rather than the mono the numbers use.
      style={{ fontFamily: "inherit" }}
      value={state.value == null ? "" : String(state.value)}
      placeholder="Enter"
      aria-label={def.label}
      onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
    />
  );
}

/**
 * The honest label under a comp's seller-concession figure.
 *
 * Three states that look identical as a number and are completely different as evidence:
 *   - the source stated a figure (including a real $0)  -> sourced
 *   - the source said nothing, so the app assumes $0    -> an ASSUMPTION, said out loud
 *   - a human confirmed it in review                    -> the strongest of the three
 *
 * The middle one is why this component exists. `engine.amb.concession_status` returns `assumed_zero`
 * for a silent source so a blank concession can never halt a build (owner ruling 2026-07-28) — and
 * that convenience becomes a quiet lie the moment the report presents it as source-verified.
 *
 * TWO MORE STATES SINCE 2026-08-10, AND THEY WERE THE SILENT ONES. `review_required` and `blocked`
 * both fell through to `return null`, so a comparable the engine is about to REMOVE from the
 * valuation (`screen_concessions`, amb.py:419-428) sat in the grid with an unremarkable dollar
 * figure and no mark of any kind. The one row an agent most needed to look at was the one the
 * screen said least about. Neither state is a scolding and neither blocks anything here: each says
 * what the valuation will do with the row and what clears it.
 */
export function ConcessionNote({ status, value }: { status?: string; value: number | null }) {
  if (status === "review_required") {
    return (
      <span style={{ display: "block", marginTop: 2, fontSize: 11, color: colors.caution, maxWidth: 150,
                     lineHeight: 1.3, fontFamily: "system-ui, sans-serif" }}>
        Over 6% of the sale price — confirm it here or this sale is left out of the valuation
      </span>
    );
  }
  if (status === "blocked") {
    return (
      <span style={{ display: "block", marginTop: 2, fontSize: 11, color: colors.caution, maxWidth: 150,
                     lineHeight: 1.3, fontFamily: "system-ui, sans-serif" }}>
        Over 10% of the sale price — correct it; no confirmation can accept this figure
      </span>
    );
  }
  if (status === "human_confirmed") {
    return (
      <span style={{ display: "block", marginTop: 2, fontSize: 11, color: colors.verified }}>
        You confirmed this
      </span>
    );
  }
  if (status === "assumed_zero") {
    return (
      <span style={{ display: "block", marginTop: 2, fontSize: 11, color: colors.amber, maxWidth: 150, lineHeight: 1.3 }}>
        Assumed $0 — not stated in your file
      </span>
    );
  }
  if (status === "verified") {
    return (
      <span style={{ display: "block", marginTop: 2, fontSize: 11, color: colors.textSecondary }}>
        {value === 0 ? "Your file states $0" : "Stated in your file"}
      </span>
    );
  }
  return null;
}

/** The "edited" mark's hover text: what the source said, and which source said it. The origin of
 * record stays visible here after the row's tag has become "You entered". */
function editedTitle(state: { originalValue?: unknown; sourceTag: string | null }): string {
  const was = state.originalValue ?? "—";
  return state.sourceTag
    ? `As pulled from ${state.sourceTag}: ${String(was)} — corrected by you`
    : `As pulled: ${String(was)} — corrected by you`;
}
